export type Language = 'de' | 'en';

export interface Translations {
  nav: {
    about: string;
    services: string;
    aiAgents: string;
    process: string;
    industries: string;
    contact: string;
    consultation: string;
  };
  hero: {
    badge: string;
    title: string;
    titleHighlight: string;
    subtitle: string;
    cta1: string;
    cta2: string;
    gcc?: {
      badge?: string;
      title?: string;
      subtitle?: string;
    };
  };
  clientLogos: {
    header: string;
    enterpriseTitle: string;
    assuranceTitle: string;
    enterpriseItems: { name: string; logo: string }[];
    assuranceItems: string[];
    assuranceItemsGCC?: string[];
  };
  strategicAssessment: {
    tag: string;
    title: string;
    subtitle: string;
    prompt: string;
    scoreLabel: string;
    scoreSuffix: string;
    disclaimer: string;
    cta: string;
    options: {
      id: string;
      title: string;
      description: string;
      score: number;
      insight: string;
    }[];
  };
  diagnostic: {
    tag: string;
    title: string;
    subtitle: string;
    startBtn: string;
    noEmail: string;
    progress: string;
    estimatedTime: string;
    analyzing: {
      title: string;
      step1: string;
      step2: string;
      step3: string;
      step4: string;
    };
    result: {
      scoreTitle: string;
      peerTitle: string;
      downloadBtn: string;
      downloading: string;
      note: string;
      scheduleBtn: string;
      restartBtn: string;
    };
    dimensions: {
      regulatory: { name: string; description: string };
      integration: { name: string; description: string };
      ai_governance: { name: string; description: string };
      visibility: { name: string; description: string };
      strategy: { name: string; description: string };
    };
    questions: {
      id: string;
      dimension: 'regulatory' | 'integration' | 'ai_governance' | 'visibility' | 'strategy';
      text: string;
      context?: string;
      options: {
        value: number;
        label: string;
        insight: string;
      }[];
    }[];
  };
  roiCalculator: {
    title: string;
    subtitle: string;
    spendLabel: string;
    savingLabel: string;
    emailPlaceholder: string;
    cta: string;
    disclaimer: string;
    industryLabel: string;
    industries: {
      general: string;
      finance: string;
      automotive: string;
      retail: string;
    };
  };
  productSuite: {
    tag: string;
    title: string;
    subtitle: string;
    items: {
      eyebrow: string;
      title: string;
      subtitle: string;
      bullets: string[];
      cta: string;
      href: string;
    }[];
  };
  keyCapabilities: {
    title: string;
    items: { title: string; link: string }[];
  };
  about: {
    tag: string;
    title: string;
    titleHighlight: string;
    description1: string;
    description2: string;
    card1Title: string;
    card1Text: string;
    card2Title: string;
    card2Text: string;

  };
  services: {
    tag: string;
    title: string;
    subtitle: string;
    hero: {
      title: string;
      subtitle: string;
      cta: string;
    };
    pillars: {
      strategy: { title: string; items: string[] };
      ai: { title: string; items: string[] };
      erp: { title: string; items: string[] };
      software: { title: string; items: string[] };
    };
    outcomes: {
      title: string;
      items: string[];
    };
    ai: {
      title: string;
      items: string[];
    };
    erp: {
      title: string;
      items: string[];
    };
    software: {
      title: string;
      items: string[];
    };
    cloud: {
      title: string;
      items: string[];
    };
    grc: {
      title: string;
      items: string[];
    };
    changeManagement: {
      title: string;
      items: string[];
      itemsGCC?: string[];
    };
  };
  aiAgents: {
    tag: string;
    title: string;
    subtitle: string;
    agents: {
      document: { title: string; desc: string };
      logistics: { title: string; desc: string };
      supply: { title: string; desc: string };
      finance: { title: string; desc: string };
      field: { title: string; desc: string };
      support: { title: string; desc: string };
    };
    tech: {
      llm: string;
      rag: string;
      cloud: string;
    };
  };
  process: {
    tag: string;
    title: string;
    subtitle: string;
    steps: {
      step1: {
        num: string;
        title: string;
        subtitle: string;
        items: string[];
      };
      step2: {
        num: string;
        title: string;
        subtitle: string;
        items: string[];
      };
      step3: {
        num: string;
        title: string;
        subtitle: string;
        items: string[];
      };
      step4: {
        num: string;
        title: string;
        subtitle: string;
        items: string[];
      };
      step5: {
        num: string;
        title: string;
        subtitle: string;
        items: string[];
      };
      step6: {
        num: string;
        title: string;
        subtitle: string;
        items: string[];
      };
    };
  };
  industries: {
    tag: string;
    title: string;
    subtitle: string;
    items: {
      icon: string;
      title: string;
      description: string;
    }[];
  };
  contact: {
    tag: string;
    title: string;
    subtitle: string;
    helper?: string;
    form: {
      name: string;
      email: string;
      company: string;
      phone?: string;
      subject?: string;
      message: string;
      submit: string;
      consentLabel?: string;
      consentLink?: string;
      consentLabelSuffix?: string;
      consentRequired?: string;
      workEmailError?: string;
      submitError?: string;
      submitErrorButton?: string;
      calendlyCta?: string;
      secureBadge?: string;
      subjects?: {
        consultation: string;
        project: string;
        partnership: string;
        other: string;
      };
    };
    info: {
      address: string;
      email: string;
      phone?: string;
    };
    consultation?: {
      title: string;
      desc: string;
      btn: string;
    };
  };
  insightsTeaser: {
    tag: string;
    title: string;
    subtitle: string;
    cta: string;
    items: { eyebrow: string; title: string; desc: string; href: string }[];
  };
  homeProcess: {
    tag?: string;
    title: string;
    subtitle: string;
    steps: { title: string; subtitle?: string; items?: string[]; desc?: string }[];
  };
  outcomes: {
    tag: string;
    title: string;
    subtitle: string;
    items: {
      icon: string;
      kpi: string;
      title: string;
      desc: string;
      link: string;
    }[];
  };
  proof: {
    tag: string;
    title: string;
    subtitle: string;
    artifacts: {
      title: string;
      subtitle: string;
      items: { title: string; desc: string }[];
    };
    stats?: { value: string; label: string }[];
    labels: { boardroomChallenge: string; impact: string };
    cases: {
      title: string;
      industry: string;
      boardroomChallenge: string;
      impact: string;
      metric?: string;
      metricLabel?: string;
      badge?: string;
    }[];
    actions: { downloadCase: string; downloadSecurity: string; cta: string; note: string; readCase: string };
    certificates: {
      title: string;
      items: { name: string; icon: string }[];
    };
  };
  engagement: {
    tag: string;
    title: string;
    subtitle: string;

    stats?: { label: string; value: string }[];
    steps: { title: string; desc: string }[];
    personas: string[];
    budgets: string[];
  };
  footer: {
    company: string;
    description: string;
    links: {
      company: string;
      legal: string;
      social: string;
      privacy: string;
      impressum: string;
    };
    copyright: string;
  };
  data: {
    title: string;
    items: string[];
  };
  aiAutomationPage: {
    hero: { title: string; subtitle: string };
    capabilities: { title: string; items: { title: string; desc: string }[] };
    outcomes: { title: string; items: string[] };
    process: { title: string; steps: string[] };
    faq: { title: string; items: { q: string; a: string }[] };
  };
  erpSapPage: {
    hero: { title: string; subtitle: string };
    capabilities: { title: string; items: { title: string; desc: string }[] };
    outcomes: { title: string; items: string[] };
    process: { title: string; steps: string[] };
    faq: { title: string; items: { q: string; a: string }[] };
  };
  softwareDevPage: {
    hero: { title: string; subtitle: string };
    capabilities: { title: string; items: { title: string; desc: string }[] };
    outcomes: { title: string; items: string[] };
  };
  industriesPage: {
    hero: { title: string; subtitle: string };
    items: { title: string; desc: string }[];
    pillars: {
      automotive: { title: string; items: string[] };
      energy: { title: string; items: string[] };
      logistics: { title: string; items: string[] };
      fintech: { title: string; items: string[] };
      manufacturing: { title: string; items: string[] };
    };
  };
  companyPage: {
    hero: { title: string; subtitle: string; headline?: string; subheadline?: string; intro?: string };
    story: { title: string; content: string; problems: string[] };
    reality: { title: string; content: string; implications: string[] };
    focus: { text: string };
    differentiation: { title: string; items: string[] };
    principles: { title: string; items: string[]; goal: string };
    team: { title: string; content: string; workingStyle: string[]; growth: string };
    dach: { title: string; content: string[]; benefit: string };
    partnership: { title: string; text: string; roles: string[]; summary: string };
    values: { title: string; items: { name: string; subline: string }[] };
  };
  servicesOverview: {
    sectionTitle: string;
    services: { title: string; href: string }[];
  };
  methodology: {
    title: string;
    subtitle: string;
    phases: {
      discover: { title: string; copy: string; };
      design: { title: string; copy: string; };
      deploy: { title: string; copy: string; };
      evolve: { title: string; copy: string; };
    };
    pillars: {
      governance: string;
      data: string;
      technology: string;
    };
    cta: {
      headline: string;
      button: string;
      subtext: string;
    };
  };
  accessibility: {
    shortcuts: {
      title: string;
      navigation: string;
      toggleMenu: string;
      scrollDown: string;
      general: string;
      showHelp: string;
    };
  };
}

export const translations: Record<Language, Translations> = {
  de: {
    nav: {
      about: 'Über uns',
      services: 'Leistungen',
      aiAgents: 'AI Agents',
      process: 'Prozess',
      industries: 'Branchen',
      contact: 'Kontakt',
      consultation: 'Beratung anfragen',
    },
    hero: {
      badge: 'Strategischer Enterprise IT-Partner',
      title: 'Enterprise IT-Lösungen für messbaren ROI',
      titleHighlight: '',
      subtitle:
        'Modernisierung von Enterprise-Systemen für DACH-Entscheider. Maximale Sicherheit. Messbare Effizienz.',
      cta1: 'Kostenloses Strategic Assessment starten',
      cta2: 'Wie funktioniert das?',
    },
    clientLogos: {
      header: 'Von globalen Marktführern vertraut',
      enterpriseTitle: 'Enterprise Excellence',
      assuranceTitle: 'Zertifizierte Sicherheit',
      enterpriseItems: [
        { name: 'SAP', logo: '/images/partners/sap.svg' },
        { name: 'Microsoft', logo: '/images/partners/microsoft.svg' },
        { name: 'VMware', logo: '/images/partners/vmware.svg' },
        { name: 'Salesforce', logo: '/images/partners/salesforce.svg' },
        { name: 'Docker', logo: '/images/partners/docker.png' },
        { name: 'Kubernetes', logo: '/images/partners/kubernetes.png' },
        { name: 'GitHub', logo: '/images/partners/github.png' },
        { name: 'GitLab', logo: '/images/partners/gitlab.png' },
        { name: 'PostgreSQL', logo: '/images/partners/postgresql.png' },
        { name: 'Apache Kafka', logo: '/images/partners/kafka.png' },
        { name: 'ISO 27001 Certified', logo: '/images/partners/iso27001.png' },
        { name: 'AWS Partner', logo: '/images/partners/aws.svg' },
      ],
      assuranceItems: [
        'Zertifizierte Cloud-Experten',
        'Audit-Ready Compliance',
        'Enterprise-Grade Security',
        'DSGVO Konformität',
        'Sichere Infrastrukturen',
      ],
    },
    strategicAssessment: {
      tag: 'Strategische Analyse',
      title: 'Kostenloses IT-Assessment',
      subtitle:
        'Verschaffen Sie sich sofort Einblicke in Ihre IT-Umgebung mit unseren kostenlosen Assessments.',
      prompt: 'Assessment-Fokus wählen',
      scoreLabel: 'Readiness Score',
      scoreSuffix: '/100',
      disclaimer: 'Unverbindliche Ersteinschätzung auf Basis Ihrer Angaben.',
      cta: 'Assessment starten',
      options: [
        {
          id: 'readiness',
          title: 'Digital Transformation Readiness',
          description: 'Status-Quo, Roadmap & ROI-Check.',
          score: 85,
          insight: 'Oft fehlt eine klare Roadmap für skalierbare Transformation.',
        },
        {
          id: 'cloud',
          title: 'Cloud Migration Audit',
          description: 'Kosten-Effizienz & Architektur-Prüfung.',
          score: 65,
          insight: 'Legacy-Systeme erhöhen OpEx unnötig.',
        },
        {
          id: 'security',
          title: 'IT Security Health Check',
          description: 'Risiko-, Compliance- & Lücken-Analyse.',
          score: 45,
          insight: 'Sicherheitslücken sind oft in Prozessebenen versteckt.',
        },
      ],
    },
    diagnostic: {
      tag: 'Kostenlose Analyse',
      title: 'Enterprise Maturity Assessment',
      subtitle: 'Benchmarken Sie Ihre Organisation gegen DACH-Unternehmen. 10 strategische Fragen, sofortiger PDF-Report mit konkreten Handlungsempfehlungen.',
      startBtn: 'Assessment starten',
      noEmail: 'Keine E-Mail erforderlich • Sofortiger PDF-Download',
      progress: 'Fortschritt',
      estimatedTime: '~3 Minuten',
      analyzing: {
        title: 'Analysiere Enterprise-Architektur...',
        step1: 'Bewerte Compliance-Reife...',
        step2: 'Analysiere Integrationslandschaft...',
        step3: 'Vergleiche mit Industrie-Benchmarks...',
        step4: 'Generiere Handlungsempfehlungen...',
      },
      result: {
        scoreTitle: 'Ihr Reifegrad',
        peerTitle: 'DACH Durchschnitt',
        downloadBtn: 'Report herunterladen (PDF)',
        downloading: 'PDF wird erstellt...',
        note: 'Ihr Report enthält dimensionsspezifische Analysen und priorisierte Empfehlungen.',
        scheduleBtn: 'Ergebnisse besprechen',
        restartBtn: 'Neu starten',
      },
      dimensions: {
        regulatory: {
          name: 'Regulatorische Bereitschaft',
          description: 'DORA, NIS2 & EU AI Act Compliance-Fähigkeit',
        },
        integration: {
          name: 'Integrationsarchitektur',
          description: 'Legacy-Systeme, technische Schulden & Modernisierung',
        },
        ai_governance: {
          name: 'KI-Governance',
          description: 'Kontrolle über KI-Nutzung & Shadow-AI-Risiken',
        },
        visibility: {
          name: 'Operative Transparenz',
          description: 'Echtzeit-Einblick in Assets, Kosten & Abhängigkeiten',
        },
        strategy: {
          name: 'Strategische Ausrichtung',
          description: 'SAP-Roadmap, Clean Core & Transformationsplanung',
        },
      },
      questions: [
        {
          id: 'reg_reporting',
          dimension: 'regulatory',
          text: 'Wie erfolgt Ihr DORA/NIS2 Compliance-Reporting aktuell?',
          context: 'Ab Januar 2025 müssen betroffene Unternehmen nachweisbare Prozesse vorhalten.',
          options: [
            {
              value: 20,
              label: 'Automatisiert mit Echtzeit-Dashboards',
              insight: 'Vorbildliche Automatisierung – reduziert Audit-Aufwand um bis zu 70%',
            },
            {
              value: 15,
              label: 'Teilautomatisiert, quartalsweise Reports',
              insight: 'Gute Basis, aber manuelle Lücken erhöhen Risiko bei kurzfristigen Audits',
            },
            {
              value: 8,
              label: 'Überwiegend manuell via Excel/Confluence',
              insight: 'Typisch für Mittelstand, aber nicht skalierbar für DORA-Anforderungen',
            },
            {
              value: 0,
              label: 'Kein strukturierter Prozess vorhanden',
              insight: 'Kritische Lücke – regulatorisches Risiko ab Q1 2025',
            },
          ],
        },
        {
          id: 'reg_ict_risk',
          dimension: 'regulatory',
          text: 'Können Sie ICT-Risiken Dritter (Cloud, SaaS, Outsourcing) nachvollziehbar dokumentieren?',
          options: [
            {
              value: 20,
              label: 'Vollständiges Vendor-Risk-Management mit kontinuierlichem Monitoring',
              insight: 'DORA-konform – ermöglicht schnelle Reaktion auf Vorfälle bei Drittanbietern',
            },
            {
              value: 12,
              label: 'Verträge und SLAs dokumentiert, kein aktives Monitoring',
              insight: 'Dokumentation vorhanden, aber reaktive Haltung bei Störungen',
            },
            {
              value: 5,
              label: 'Teilweise dokumentiert, abhängig vom Anbieter',
              insight: 'Fragmentierte Sicht erhöht Risiko bei Ausfällen kritischer Dienste',
            },
            {
              value: 0,
              label: 'Keine systematische Erfassung',
              insight: 'Wesentliche DORA-Anforderung nicht erfüllt',
            },
          ],
        },
        {
          id: 'int_legacy',
          dimension: 'integration',
          text: 'Wie viele Ihrer geschäftskritischen Prozesse hängen von Legacy-Schnittstellen ab (BAPI, RFC, IDOC, Flat Files)?',
          options: [
            {
              value: 20,
              label: 'Unter 20% – überwiegend moderne APIs',
              insight: 'Zukunftssichere Architektur mit geringer technischer Schuld',
            },
            {
              value: 12,
              label: '20-50% – Mischlandschaft',
              insight: 'Typisch für SAP-Kunden – priorisierte Migration empfohlen',
            },
            {
              value: 5,
              label: '50-80% – Legacy dominiert',
              insight: 'Erhebliche Integration Debt – blockiert Innovationsgeschwindigkeit',
            },
            {
              value: 0,
              label: 'Über 80% – kaum moderne Schnittstellen',
              insight: 'Kritische Abhängigkeit von Expertise, die zunehmend schwerer verfügbar ist',
            },
          ],
        },
        {
          id: 'int_documentation',
          dimension: 'integration',
          text: 'Ist Ihre Schnittstellenlandschaft vollständig dokumentiert und für Entwickler zugänglich?',
          options: [
            {
              value: 20,
              label: 'Ja, API-Katalog mit OpenAPI-Specs und Self-Service-Portal',
              insight: 'Ermöglicht schnelle Onboarding neuer Entwickler und Partner',
            },
            {
              value: 12,
              label: 'Teilweise dokumentiert in Confluence/SharePoint',
              insight: 'Dokumentation existiert, aber Suche und Aktualität sind Herausforderungen',
            },
            {
              value: 5,
              label: 'Nur in den Köpfen der Experten',
              insight: 'Kritisches Wissensrisiko – Key-Person-Dependencies',
            },
            {
              value: 0,
              label: 'Unbekannt / Keine Übersicht',
              insight: 'Shadow-Integration-Risiko – niemand kennt die vollständige Landschaft',
            },
          ],
        },
        {
          id: 'ai_inventory',
          dimension: 'ai_governance',
          text: 'Haben Sie eine Übersicht aller KI-Tools und LLMs, die in Ihrem Unternehmen genutzt werden?',
          context: 'EU AI Act verlangt ab 2025 Transparenz über KI-Einsatz.',
          options: [
            {
              value: 20,
              label: 'Vollständiges KI-Inventar mit Risikobewertung pro Use Case',
              insight: 'EU AI Act ready – Grundlage für verantwortungsvolle KI-Skalierung',
            },
            {
              value: 12,
              label: 'Offizielle Tools bekannt, aber keine Shadow-AI-Erfassung',
              insight: 'Governance-Lücke bei nicht-genehmigten Tools (ChatGPT, Copilot etc.)',
            },
            {
              value: 5,
              label: 'Nur IT-gesteuerte Projekte erfasst',
              insight: 'Fachbereiche nutzen wahrscheinlich eigene Tools ohne Oversight',
            },
            {
              value: 0,
              label: 'Keine zentrale Übersicht',
              insight: 'Signifikantes Compliance- und Datenschutzrisiko durch unkontrollierte KI-Nutzung',
            },
          ],
        },
        {
          id: 'ai_production',
          dimension: 'ai_governance',
          text: 'Sind KI/ML-Modelle in Ihren produktiven Geschäftsprozessen integriert?',
          options: [
            {
              value: 20,
              label: 'Ja, mehrere Use Cases in Produktion mit MLOps-Pipeline',
              insight: 'Wettbewerbsvorteil durch operationalisierte KI',
            },
            {
              value: 15,
              label: 'Einzelne Use Cases live, ohne standardisierte Pipeline',
              insight: 'Guter Start, aber Skalierbarkeit und Wartbarkeit sind Risiken',
            },
            {
              value: 8,
              label: 'Nur Pilots und PoCs, nichts in Produktion',
              insight: 'Typische "PoC-Falle" – fehlender Weg zur Produktivierung',
            },
            {
              value: 0,
              label: 'Kein aktiver KI-Einsatz',
              insight: 'Wettbewerbsnachteil in Branchen mit zunehmender KI-Adoption',
            },
          ],
        },
        {
          id: 'vis_assets',
          dimension: 'visibility',
          text: 'Pflegen Sie ein automatisiertes Inventar aller Software-Assets und Cloud-Ressourcen?',
          options: [
            {
              value: 20,
              label: 'Ja, automatisiert mit CMDB-Integration und Dependency-Mapping',
              insight: 'Voraussetzung für Incident Response und Change Management',
            },
            {
              value: 12,
              label: 'Teilautomatisiert, aber nicht alle Systeme erfasst',
              insight: 'Blinde Flecken erhöhen MTTR bei Incidents',
            },
            {
              value: 5,
              label: 'Manuell gepflegte Listen (Excel, ServiceNow)',
              insight: 'Hoher Pflegeaufwand, Aktualität fraglich',
            },
            {
              value: 0,
              label: 'Keine systematische Erfassung',
              insight: 'DORA-Anforderung nicht erfüllt – kein vollständiges Bild der Angriffsfläche',
            },
          ],
        },
        {
          id: 'vis_costs',
          dimension: 'visibility',
          text: 'Haben Sie Echtzeit-Transparenz über Cloud-Kosten und SaaS-Ausgaben?',
          options: [
            {
              value: 20,
              label: 'Ja, FinOps-Dashboard mit Anomalie-Erkennung und Forecasting',
              insight: 'Ermöglicht proaktive Kostenoptimierung – typisch 20-30% Einsparpotenzial',
            },
            {
              value: 12,
              label: 'Monatliche Auswertungen, aber keine Echtzeit-Sicht',
              insight: 'Reaktive Kostenkontrolle – Überraschungen bei Rechnungsstellung',
            },
            {
              value: 5,
              label: 'Nur aggregierte Zahlen aus Buchhaltung',
              insight: 'Keine Zuordnung zu Teams/Projekten – Cost Allocation fehlt',
            },
            {
              value: 0,
              label: 'Keine dedizierte Kostenübersicht',
              insight: 'Cloud-Kostenwachstum unkontrolliert – häufig 30-50% über Budget',
            },
          ],
        },
        {
          id: 'str_sap',
          dimension: 'strategy',
          text: 'Wie ist Ihre SAP S/4HANA Roadmap bezüglich Clean Core definiert?',
          context: 'Clean Core ist entscheidend für Update-Fähigkeit und Support nach 2027.',
          options: [
            {
              value: 20,
              label: 'Strikte Clean-Core-Strategie mit Side-by-Side-Extensions',
              insight: 'Zukunftssichere Architektur – ermöglicht kontinuierliche SAP-Updates',
            },
            {
              value: 15,
              label: 'Clean Core geplant, Migration läuft',
              insight: 'Auf gutem Weg – Priorität auf kritische Custom Code Bereinigung legen',
            },
            {
              value: 8,
              label: 'S/4HANA Roadmap existiert, Clean Core nicht priorisiert',
              insight: 'Risiko: Embedded Extensions erschweren zukünftige Upgrades',
            },
            {
              value: 0,
              label: 'Noch auf ECC / Keine klare S/4-Strategie',
              insight: 'Handlungsdruck durch SAP ECC Supportende 2027/2030',
            },
          ],
        },
        {
          id: 'str_transform',
          dimension: 'strategy',
          text: 'Wie bewerten Sie Ihre Fähigkeit, neue Geschäftsanforderungen IT-seitig umzusetzen?',
          options: [
            {
              value: 20,
              label: 'Agil – neue Features in Wochen, nicht Monaten',
              insight: 'Wettbewerbsrelevante Time-to-Market',
            },
            {
              value: 12,
              label: 'Durchschnittlich – abhängig von Projektgröße und Legacy-Abhängigkeiten',
              insight: 'Typisch für gewachsene Landschaften – selektive Modernisierung empfohlen',
            },
            {
              value: 5,
              label: 'Langsam – oft 6+ Monate für mittlere Änderungen',
              insight: 'Integration Debt bremst Innovation',
            },
            {
              value: 0,
              label: 'Sehr eingeschränkt – IT ist primär im "Keep the Lights On" Modus',
              insight: 'Strategische Handlungsfähigkeit gefährdet',
            },
          ],
        },
      ],
    },
    roiCalculator: {
      title: 'ROI Rechner',
      subtitle: 'Berechnen Sie Ihr Einsparpotenzial durch IT-Modernisierung.',
      spendLabel: 'Aktuelle IT-Ausgaben (jährlich)',
      savingLabel: 'Mögliche OpEx-Einsparung',
      emailPlaceholder: 'Geschäftliche E-Mail',
      cta: 'ROI-Report anfordern',
      disclaimer: 'Schätzung basierend auf Branchen-Benchmarks.',
      industryLabel: 'Branche',
      industries: {
        general: 'Allgemein',
        finance: 'Banken & Finanzwesen',
        automotive: 'Automobilindustrie',
        retail: 'Einzelhandel',
      },
    },
    productSuite: {
      tag: 'Strategische Fokusbereiche',
      title: 'Drei Programme für Enterprise-Exzellenz',
      subtitle:
        'Planbare Kosten, belastbare Governance und moderne ERP-Landschaften.',
      items: [
        {
          eyebrow: 'Software Asset Management',
          title: 'Strategisches Software Asset Management',
          subtitle: 'VMware-Kosten- und Sourcing-Strategie.',
          bullets: [
            'Asset- & Lizenz-Baseline für Verhandlungen.',
            'Sourcing-Szenarien mit TCO-Impact.',
            'Verhandlungs- & Migrations-Roadmap.',
          ],
          cta: 'Asset-Management-Audit anfragen',
          href: '/products/license-defense',
        },
        {
          eyebrow: 'GRC & Compliance',
          title: 'Enterprise Governance & Digital Resilience',
          subtitle: 'GRC-Framework nach DORA/NIS2.',
          bullets: [
            'Kontrollframework & RoI-Governance.',
            'Regulatory-Reporting-Operating-Model.',
            'Procurement-ready Policies & Evidence.',
          ],
          cta: 'GRC-Assessment anfragen',
          href: '/products/officer-shield',
        },
        {
          eyebrow: 'ERP Modernisierung',
          title: 'Strategische ERP-Modernisierung',
          subtitle: 'Clean Core als Fundament für S/4HANA, AI und Cloud.',
          bullets: [
            'Usage-basierte Rationalisierung von Z-Code.',
            'Betriebsrat-konformer Privacy Proxy.',
            '2027+ Roadmap & Business Case.',
          ],
          cta: 'ERP-Modernisierungs-Assessment anfragen',
          href: '/products/clean-core-surgeon',
        },
      ],
    },
    keyCapabilities: {
      title: 'Service Portfolio & Delivery',
      items: [
        { title: 'AI Automation & Analytics', link: '/services/ai-automation' },
        { title: 'ERP & SAP Integration', link: '/services/erp-sap' },
        { title: 'Enterprise Software Engineering', link: '/services/software-engineering' },
        { title: 'Cloud & Platform Foundations', link: '/services/cloud-platform-foundations' },
        { title: 'Security, Privacy & Governance', link: '/services/security-privacy-governance' },
        { title: 'Change Management & Delivery', link: '/services/change-management-delivery' },
      ],
    },
    about: {
      tag: 'STRATEGISCHER ENTERPRISE IT-PARTNER',
      title: 'Strategischer',
      titleHighlight: 'Lösungsanbieter',
      description1:
        'Engineering-First Ansatz, der Tech-Debt für 10+ DACH-Unternehmen reduziert hat. Durchschnittliche Projekteinsparungen von 28%.',
      description2:
        'Value-Driven Engineering über Software-Assets, Governance und ERP-Modernisierung – mit messbaren Ergebnissen: 100% Audit-Success-Rate und durchschnittlich 60% schnellere Time-to-Value.',
      card1Title: 'Enterprise Value',
      card1Text:
        'OpEx planbar machen und Budgets langfristig sichern. Ø 5:1 ROI im ersten Jahr.',
      card2Title: 'Governance Excellence',
      card2Text:
        'ISO 27001 & TISAX Audit-Ready. 100% Compliance-Success-Rate bei unseren Kunden.',

    },
    services: {
      tag: 'Kernkompetenzen',
      title: 'Maßgeschneiderte Enterprise-Services',
      subtitle: 'Skalierbare Lösungen für Ihre digitale Transformation.',
      hero: {
        title: 'Strategische Enterprise Services',
        subtitle: 'Beratung und Delivery für Software-Assets, Governance und ERP-Modernisierung.',
        cta: 'Service Portfolio ansehen',
      },
      pillars: {
        strategy: {
          title: 'Strategie & Transformation',
          items: ['Enterprise Roadmap', 'Program Governance', 'Operating Model', 'Change Enablement'],
        },
        ai: {
          title: 'KI & Data Analytics',
          items: ['AI Strategy & Use Cases', 'Data Quality & MDM', 'Advanced Analytics & BI', 'MLOps & Governance'],
        },
        erp: {
          title: 'SAP & ERP Transformation',
          items: ['S/4HANA Roadmap & Architektur', 'Process Harmonization', 'Integration & API Layer', 'Clean-Core Governance'],
        },
        software: {
          title: 'Digital Workplace',
          items: ['Enterprise Apps & Portale', 'Collaboration Automation', 'Identity & Access', 'Adoption & Enablement'],
        },
      },
      outcomes: {
        title: 'Ergebnisse',
        items: [
          'Planbare Kosten und klare Verantwortlichkeiten',
          'Audit-Ready Governance und Compliance',
          'Modernisierte ERP-Landschaften',
          'Datengetriebene Entscheidungsfähigkeit',
          'Transparenz über Systeme und Vendoren hinweg',
        ],
      },
      software: {
        title: 'Individuelle Softwareentwicklung',
        items: [
          'Java & Enterprise Stacks nach Maß.',
          'Skalierbare Frontend- & Backend-Architekturen.',
          'Performance-kritische Anwendungen.',
        ],
      },
      cloud: {
        title: 'Cloud & DevOps Lösungen',
        items: [
          'Migration und Infrastruktur-Automatisierung.',
          '50% schnellere Deployment-Zeiten.',
          'Kostenoptimierte Cloud-Architekturen.',
        ],
      },
      erp: {
        title: 'SAP Modernisierung',
        items: [
          'Effizienzsteigerung durch System-Upgrades.',
          'Prozessoptimierung und Clean Core.',
          'Bessere Data Insights.',
        ],
      },
      ai: {
        title: 'AI & Data Strategie',
        items: [
          'Intelligente Agenten zur Entscheidungsfindung.',
          'Datenplattformen für Automatisierung.',
          'Messbare ROI-Szenarien.',
        ],
      },
      grc: {
        title: 'Governance, Risk & Compliance',
        items: [
          'IT-Sicherheits-Audits und Frameworks.',
          'DORA/NIS2 Compliance-Readiness.',
          'Integriertes Risikomanagement.',
        ],
      },
      changeManagement: {
        title: 'AI Adoption & Change Management',
        items: [
          'Workforce-ready AI & Kultur.',
          'Prosci-konforme Frameworks.',
          'Betriebsrat-sichere Einführung.',
        ],
        itemsGCC: [
          'Workforce-ready AI & Kultur.',
          'Prosci-konforme Frameworks.',
          'PDPL-konformer Privacy Proxy.',
        ],
      },
    },
    aiAgents: {
      tag: 'AI Agents',
      title: 'Intelligente Agenten für Ihr Unternehmen',
      subtitle:
        'Maßgeschneiderte KI-Agenten, die direkt in SAP, ERP und Enterprise-Systeme integriert werden',
      agents: {
        document: {
          title: 'Dokumentenverarbeitung',
          desc: 'Automatische Extraktion & Klassifizierung',
        },
        logistics: {
          title: 'Logistikassistent',
          desc: 'Intelligente Routenplanung & Tracking',
        },
        supply: {
          title: 'Supply Chain Forecasting',
          desc: 'Predictive Bestandsmanagement',
        },
        finance: {
          title: 'Finance Copilot',
          desc: 'Automatisierte Berichte & Analysen',
        },
        field: {
          title: 'Field Service Copilot',
          desc: 'Mobile Einsatzplanung & Support',
        },
        support: {
          title: 'Customer Support AI',
          desc: 'Intelligenter 24/7-Kundenservice',
        },
      },
      tech: {
        llm: 'LLMs (GPT-Architekturen)',
        rag: 'RAG-Pipelines',
        cloud: 'Cloud & On-Premise',
      },
    },
    process: {
      tag: 'Prozess',
      title: 'Unser 6-Schritte-Prozess',
      subtitle: 'Von der Strategie zur skalierbaren Ausführung',
      steps: {
        step1: {
          num: '01',
          title: 'Discovery & Strategie',
          subtitle: 'Technologie auf Geschäftsziele ausrichten',
          items: ['Bedarfsanalyse', 'Machbarkeitsstudie', 'Strategische Roadmap'],
        },
        step2: {
          num: '02',
          title: 'Analyse & Architektur',
          subtitle: 'Design des digitalen Ökosystems',
          items: ['Systemaudit', 'Lösungsarchitektur', 'Tech-Stack-Auswahl'],
        },
        step3: {
          num: '03',
          title: 'Entwicklung & Integration',
          subtitle: 'Aufbau der Kerninfrastruktur',
          items: ['Agile Entwicklung', 'API-Integration', 'Sicheres Coding'],
        },
        step4: {
          num: '04',
          title: 'KI-Modelltraining',
          subtitle: 'Implementierung von Intelligenz',
          items: ['Datenaufbereitung', 'Modell-Feintuning', 'Performance-Optimierung'],
        },
        step5: {
          num: '05',
          title: 'Testing & Validierung',
          subtitle: 'Sicherheit und Zuverlässigkeit gewährleisten',
          items: ['QA Testing', 'Sicherheitsaudits', 'User Acceptance Testing'],
        },
        step6: {
          num: '06',
          title: 'Deployment & Skalierung',
          subtitle: 'Go-Live und Wachstum',
          items: ['Nahtloses Deployment', 'Monitoring', 'Kontinuierliche Verbesserung'],
        },
      },
    },
    outcomes: {
      tag: 'Wertbeitrag',
      title: 'Messbarer Wertbeitrag für Vorstand und Bereiche',
      subtitle: 'Planbare Kosten, belastbare Governance, moderne Plattformen.',
      items: [
        {
          icon: 'piggyBank',
          kpi: '30%',
          title: 'OpEx-Reduktion',
          desc: 'Software-Asset-Baselines schaffen Kostentransparenz und reduzieren ungeplante Ausgaben.',
          link: '/products/license-defense'
        },
        {
          icon: 'shield',
          kpi: '100%',
          title: 'DORA-Readiness',
          desc: 'NIS2/DORA-konforme Prozesse als Wettbewerbsvorteil – vor Inkrafttreten 2025.',
          link: '/products/officer-shield'
        },
        {
          icon: 'settings',
          kpi: '60%',
          title: 'Z-Code-Risiko gesenkt',
          desc: 'Clean-Core S/4HANA-Modernisierung mit klarer Roadmap und messbarem Fortschritt.',
          link: '/products/clean-core-surgeon'
        },
        {
          icon: 'award',
          kpi: 'ISO 27001',
          title: 'Enterprise-Standards',
          desc: 'Security, Compliance und Audit-Readiness als Default – nicht als Nachgedanke.',
          link: '/trust'
        },
      ],
    },
    proof: {
      tag: 'Trust & Compliance',
      title: 'Enterprise-Standards & Zertifizierungen',
      subtitle: 'Artefakte, Policies und Nachweise für Einkauf, Datenschutz und Audit.',
      artifacts: {
        title: 'Enterprise Standards',
        subtitle: 'Beschleunigt Freigaben und Lieferantenprüfungen.',
        items: [
          { title: 'DPA & AVV', desc: 'Vorlagen nach DSGVO inkl. TOMs.' },
          { title: 'Security Overview', desc: 'Kontrollen, IAM, Verschlüsselung, Logging.' },
          { title: 'ISO-aligned Policies', desc: 'ISO-/BSI-konforme Policies & Runbooks.' },
        ],
      },
      stats: [
        { value: '€250k–€2M', label: 'OpEx-Planung pro Asset-Review' },
        { value: '4h', label: 'Regulatorisches Reporting (DORA/NIS2)' },
        { value: '30–45 Tage', label: 'bis zur strategischen Roadmap' },
        { value: '60%', label: 'Z-Code reduziert vor S/4HANA' },
      ],
      labels: {
        boardroomChallenge: 'Boardroom-Herausforderung',
        impact: 'Executive Impact',
      },
      actions: {
        downloadCase: 'Strategy Brief (PDF)',
        downloadSecurity: 'Security Overview (PDF)',
        cta: 'Strategisches Gespräch buchen',
        readCase: 'Case Study lesen',
        note: 'Referenzen unter NDA verfügbar; Kennzahlen anonymisiert.',
      },
      cases: [
        {
          title: 'Industriekonzern',
          industry: 'Fertigung',
          badge: 'ISO Zertifiziert',
          metric: '28%',
          metricLabel: 'Konsolidierung',
          boardroomChallenge: 'VMware-Preisschwankungen gefährdeten 12% des IT-Budgets.',
          impact: 'Sicherte einen 3-Jahres-Preisrahmen und lieferte 28% Konsolidierung.',
        },
        {
          title: 'Versicherungsgruppe',
          industry: 'Reguliert',
          badge: 'Audited Report',
          metric: '100%',
          metricLabel: 'Revisionssicher',
          boardroomChallenge: 'DORA/NIS2-Berichterstattung war fragmentiert und nicht revisionssicher.',
          impact: 'Ermöglichte <4 Stunden regulatorisches Reporting und revisionssichere Richtlinien.',
        },
        {
          title: 'Globaler Hersteller',
          industry: 'SAP',
          badge: 'SAP Gold Partner',
          metric: '60%',
          metricLabel: 'Z-Code Reduziert',
          boardroomChallenge: 'S/4HANA-Programm stand vor unkontrolliertem Custom-Code-Risiko.',
          impact: 'Identifizierte 60% der Z-Code-Kandidaten und sicherte die Roadmap für 2027.',
        },
      ],
      certificates: {
        title: 'Zertifizierte Sicherheit & Compliance',
        items: [
          { name: 'ISO 27001', icon: 'ShieldCheck' },
          { name: 'TISAX Ready', icon: 'Car' },
          { name: 'SOC 2 Type II', icon: 'FileCheck2' },
        ],
      },
    },
    engagement: {
      tag: 'Zusammenarbeit',
      title: 'Strategische Roadmap, klare Umsetzung',
      subtitle: 'Assessment, Blueprint, Delivery – ohne Overhead.',
      steps: [
        { title: 'Assessment (2–4 Wochen)', desc: 'Asset- und Prozessanalyse, Stakeholder-Alignment.' },
        { title: 'Strategischer Blueprint (4–6 Wochen)', desc: 'Sourcing-, Governance- und Modernisierungsplan.' },
        { title: 'Delivery & Enablement (laufend)', desc: 'Umsetzung, PMO-Integration, Steering-Committee Reporting.' },
      ],
      personas: [
        'CFO/CIO/CISO als Sponsor',
        'Procurement & Vendor Management',
        'GRC & Internal Audit',
        'SAP/ERP Programmleitung',
      ],
      budgets: [
        'Asset-Management-Audit: 25–60k EUR',
        'GRC Framework Setup: 60–150k EUR',
        'ERP Modernization Assessment: 120–350k EUR',
        'Enterprise Rollout: abhängig von Standorten',
      ],
    },
    homeProcess: {
      tag: 'Arbeitsweise',
      title: 'Value-Driven Delivery',
      subtitle: 'Assessment, Blueprint, Execution – abgestimmt auf Enterprise-Programme.',
      steps: [
        {
          title: 'Assessment',
          subtitle: 'Transparenz über Kosten und Risiken',
          items: ['Asset & Lizenz-Readout', 'Regulatory Gap-Analyse', 'Stakeholder-Alignment'],
        },
        {
          title: 'Blueprint',
          subtitle: 'Strategischer Zielzustand',
          items: ['Sourcing- und GRC-Design', 'ERP-Modernisierungsplan', 'Business-Case'],
        },
        {
          title: 'Execution',
          subtitle: 'Implementierung mit Governance',
          items: [
            'Programme & Workstreams',
            'PMO-Integration & Steering-Committee Reporting',
            'Operating Model & Runbooks',
          ],
        },
      ],
    },

    insightsTeaser: {
      tag: 'Executive Resource Center',
      title: 'Briefings für CFO, CISO und CIO',
      subtitle:
        'Persona-spezifische Entscheidungsgrundlagen für Risiko, Kosten und Transformation.',
      cta: 'Briefing ansehen →',
      items: [
        {
          eyebrow: 'Für CFO',
          title: 'OpEx-Transparenz durch Data Quality & Governance',
          desc: 'Wie Datenqualität die Budget- und Risiko-Planung stabilisiert.',
          href: '/insights/data-quality-ai',
        },
        {
          eyebrow: 'Für CISO',
          title: 'DORA/NIS2 Readiness als Compliance-Programm',
          desc: 'Kontrollen, Evidence und Reporting für Audit-Readiness.',
          href: '/insights/nis2-gdpr-ai',
        },
        {
          eyebrow: 'Für CIO',
          title: 'SAP Modernisierung als Clean-Core-Programm',
          desc: 'Z-Code reduzieren und S/4HANA-Roadmap sichern.',
          href: '/insights/sap-ai-modernization',
        },
      ],
    },
    aiAutomationPage: {
      hero: {
        title: 'AI Agents & Enterprise Automation — Gebaut für den realen Betrieb',
        subtitle: 'Wir entwickeln intelligente KI-Systeme, die Entscheidungen automatisieren, Workflows optimieren und messbare Effizienzgewinne freisetzen.',
      },
      capabilities: {
        title: 'Kernkompetenzen',
        items: [
          { title: 'Enterprise AI Agents', desc: 'Autonome Agenten für komplexe Aufgaben über Systeme hinweg.' },
          { title: 'NLP-Chatbots', desc: 'Natürliche Sprachverarbeitung für Kundenservice und Support.' },
          { title: 'Predictive Analytics', desc: 'Datengestützte Vorhersagen für bessere Entscheidungen.' },
          { title: 'Dokumentenverarbeitung', desc: 'Automatisierte Extraktion aus Rechnungen und Verträgen.' },
          { title: 'Workflow-Automatisierung', desc: 'Nahtlose Integration in ERP- und CRM-Landschaften.' },
        ],
      },
      outcomes: {
        title: 'Ergebnisse',
        items: [
          '40–70 % Reduktion repetitiver Arbeitslast',
          'Echtzeit-Insights statt manueller Berichte',
          'Reduzierte Betriebskosten',
          'Höhere Geschwindigkeit und Genauigkeit in Geschäftsprozessen',
        ],
      },
      process: {
        title: 'Prozess',
        steps: [
          'AI Readiness Assessment',
          'Daten- & Systemintegration',
          'Modellauswahl & Feintuning',
          'Deployment & Guardrails',
          'Monitoring & Iteration',
        ],
      },
      faq: {
        title: 'FAQ',
        items: [
          { q: 'Wie integrieren sich AI Agents in SAP?', a: 'Über sichere APIs und Konnektoren.' },
          { q: 'Können Sie On-Premise für sensible Daten deployen?', a: 'Ja, wir unterstützen On-Premise- und Private-Cloud-Lösungen.' },
          { q: 'Welche Guardrails sichern das KI-Verhalten?', a: 'Wir nutzen strenge Validierungslayer und Human-in-the-Loop.' },
          { q: 'Wie lange dauert ein typisches KI-Projekt?', a: 'Je nach Umfang 4–12 Wochen für ein MVP.' },
        ],
      },
    },
    erpSapPage: {
      hero: {
        title: 'Modernisieren und Erweitern Sie Ihre SAP-Landschaft mit KI & Cloud',
        subtitle: 'Wir helfen Unternehmen, den vollen Wert von SAP durch intelligente Automatisierung, Workflow-Engineering und nahtlose Integration zu erschließen.',
      },
      capabilities: {
        title: 'Fähigkeiten',
        items: [
          { title: 'SAP S/4HANA Engineering', desc: 'Maßgeschneiderte Entwicklung und Anpassung.' },
          { title: 'SAP Fiori UX', desc: 'Moderne, benutzerfreundliche Oberflächen.' },
          { title: 'API-Integration', desc: 'Sichere Verbindung von SAP mit Drittsystemen.' },
          { title: 'SAP-BTP-Erweiterungen', desc: 'Erweiterung der SAP-Funktionalität in der Cloud.' },
          { title: 'Migrationsunterstützung', desc: 'Unterstützung bei der Transformation zu S/4HANA.' },
          { title: 'Automatisierte Workflows', desc: 'Effizienzsteigerung durch intelligente Prozesse.' },
        ],
      },
      outcomes: {
        title: 'Ergebnisse',
        items: [
          'Schnellere Geschäftsprozesse',
          'Verbesserte Datenkonsistenz',
          'Verbesserte User Experience',
          'Reduzierte operative Engpässe',
          'Geringere technische Schulden',
        ],
      },
      process: {
        title: 'Prozess',
        steps: [
          'SAP-Landschafts-Review',
          'Integrations-Blueprint',
          'Custom Development',
          'Testing & Validierung',
          'Handover & Enablement',
        ],
      },
      faq: {
        title: 'FAQ',
        items: [
          { q: 'Unterstützen Sie hybride SAP-Umgebungen?', a: 'Ja, wir sind Experten für hybride Architekturen.' },
          { q: 'Können Sie benutzerdefinierte SAP-Fiori-Apps bauen?', a: 'Absolut, wir designen und entwickeln Fiori-Apps.' },
          { q: 'Wie integrieren Sie KI in SAP?', a: 'Durch die SAP BTP oder externe KI-Services via API.' },
        ],
      },
    },
    softwareDevPage: {
      hero: {
        title: 'Enterprise Software für Skalierung, Sicherheit & Performance',
        subtitle: 'Wir designen und liefern geschäftskritische Anwendungen, die auf Ihre Geschäftsprozesse zugeschnitten sind.',
      },
      capabilities: {
        title: 'Fähigkeiten',
        items: [
          { title: 'Web-Applikationen', desc: 'Skalierbare und sichere Weblösungen.' },
          { title: 'Enterprise-Systeme', desc: 'Robuste Software für geschäftskritische Prozesse.' },
          { title: 'Mobile Apps', desc: 'Native und Cross-Platform-Apps für iOS und Android.' },
          { title: 'API & Microservices', desc: 'Modulare Architekturen für Flexibilität.' },
          { title: 'Systemintegration', desc: 'Verbindung isolierter Systeme zu einer Einheit.' },
          { title: 'DevOps & Cloud', desc: 'Automatisierte Deployments und Infrastruktur.' },
        ],
      },
      outcomes: {
        title: 'Ergebnisse',
        items: [
          'Skalierbare Architektur',
          'Reduzierte Time-to-Market',
          'Erhöhte Zuverlässigkeit',
          'Nahtlose Integration über Systeme hinweg',
        ],
      },
    },
    industriesPage: {
      hero: {
        title: 'KI-gestützte Lösungen für Schlüsselbranchen',
        subtitle: 'Wir verstehen die spezifischen Herausforderungen Ihrer Industrie.',
      },
      items: [
        { title: 'Automotive', desc: 'Predictive Maintenance, Logistikoptimierung' },
        { title: 'Energie', desc: 'Smart Grid Analytics, Forecasting' },
        { title: 'Logistik', desc: 'Lagerautomatisierung, Routing' },
        { title: 'Fintech', desc: 'Betrugserkennung, Compliance-Automatisierung' },
        { title: 'Fertigung', desc: 'IIoT Analytics, Automatisierung' },
      ],
      pillars: {
        automotive: {
          title: 'Automotive & Mobilität',
          items: ['Predictive Maintenance', 'Supply Chain Optimierung', 'Qualitätssicherung mit KI', 'SAP-Integration für OEMs'],
        },
        energy: {
          title: 'Energie & Erneuerbare',
          items: ['Smart Grid Analytics', 'Verbraucherprognosen', 'Asset Performance Management', 'Netzoptimierung'],
        },
        logistics: {
          title: 'Logistik & Warehousing',
          items: ['Lagerautomatisierung', 'Routenoptimierung', 'Bestandsprognosen', 'Echtzeit-Tracking'],
        },
        fintech: {
          title: 'Fintech & Banking',
          items: ['Betrugserkennung', 'Compliance-Automatisierung', 'Risikobewertung', 'KYC-Prozesse'],
        },
        manufacturing: {
          title: 'Fertigung & IIoT',
          items: ['Produktionsoptimierung', 'Maschinenüberwachung', 'Qualitätskontrolle', 'Digital Twin'],
        },
      },
    },
    companyPage: {
      hero: {
        title: 'Über uns',
        subtitle: 'Verantwortbare IT-Expertise für CFO, CIO und CTO',
        headline: 'Wir sind keine PowerPoint-Beratung. Wir sind Architekten der neuen IT-Ära.',
        subheadline: 'Integrova AI schließt die Lücke zwischen technologischem Potenzial und operativer Realität.',
        intro: 'Wir liefern messbaren ROI durch Engineering-First Methodik. Keine Folien, keine Theorie – nur nachweisbare Ergebnisse für DACH-Enterprise-Kunden.',
      },
      story: {
        title: 'Warum Integrova AI gegründet wurde?',
        content: 'Integrova AI wurde gegründet, weil sich im Markt ein zunehmender Abstand zwischen klassischen IT-Dienstleistungsmodellen und den tatsächlichen Anforderungen von Unternehmen entwickelt hat. Viele Organisationen sehen sich seit Jahren mit denselben strukturellen Problemen konfrontiert:',
        problems: [
          'Hohe IT-Kosten durch ineffiziente Dienstleister- und Projektmodelle',
          'Lange Umsetzungszeiten trotz verfügbarer Technologien',
          'Begrenzte Produktivitäts- und Effizienzgewinne bei steigenden IT-Budgets',
          'Fehlende kontrollierte Umsetzung von KI in bestehenden Unternehmenslandschaften'
        ],
      },
      reality: {
        title: 'Eine neue Realität für Unternehmens-IT',
        content: 'Technologische Entwicklungen im Bereich KI, steigender wirtschaftlicher Druck sowie regulatorische und politische Veränderungen haben die Rahmenbedingungen für IT grundlegend verändert. Für Unternehmen bedeutet dies insbesondere:',
        implications: [
          'Höhere Anforderungen an Kostenkontrolle und Planbarkeit',
          'Erwartung schneller, belastbarer Umsetzung',
          'Notwendigkeit zur Reduktion von Komplexität und Betriebskosten',
          'Einsatz von KI nach klar definierten Unternehmensregeln',
          'Saubere Integration bestehender Systeme',
          'Kontrollierte Weiterentwicklung von SAP- und ERP-Landschaften'
        ],
      },
      focus: {
        text: 'Unser Fokus liegt darauf, IT wieder als steuerbare, wirtschaftlich sinnvolle und langfristig tragfähige Funktion im Unternehmen zu etablieren.'
      },
      differentiation: {
        title: 'Klare Abgrenzung',
        items: [
          'keine PowerPoint-Beratung ohne Umsetzungsverantwortung',
          'kein reiner Software-Entwicklungsdienstleister',
          'kein Anbieter isolierter Pilot- oder Experimentierprojekte',
          'Wir übernehmen Verantwortung für Architektur, Integration und langfristige Betriebsfähigkeit von IT-Systemen.'
        ]
      },
      principles: {
        title: 'Technologische Grundhaltung',
        items: [
          'Künstliche Intelligenz wird ausschließlich eingesetzt, wenn Ergebnisse nachvollziehbar und erklärbar sind',
          'Governance wird technisch in der Architektur verankert, nicht organisatorisch kompensiert',
          'Automatisierung erfolgt kontrolliert, prüfbar und reproduzierbar'
        ],
        goal: 'Ziel ist es, Risiken zu reduzieren, Entscheidungsprozesse abzusichern und operative Abhängigkeiten beherrschbar zu machen.'
      },
      team: {
        title: 'Erfahrung vor Größe',
        content: 'Integrova AI ist ein junges Unternehmen mit einem erfahrenen Kernteam aus Softwareentwicklern und IT-Beratern, die in unterschiedlichen Unternehmensumgebungen tätig waren.',
        workingStyle: [
          'Einsatz bewährter, moderner Technologien',
          'Fokus auf Stabilität, Wartbarkeit und Qualität',
          'Strukturierte, effiziente Umsetzung'
        ],
        growth: 'Wachstum erfolgt bewusst kontrolliert. Technische Qualität und Verlässlichkeit stehen vor schneller Skalierung.'
      },
      dach: {
        title: 'Verankert im DACH-Raum',
        content: [
          'Unser Team hat im DACH-Raum studiert und über viele Jahre gearbeitet.',
          'Wir sind mit den sprachlichen, kulturellen, organisatorischen und regulatorischen Rahmenbedingungen dieses Marktes vertraut.'
        ],
        benefit: 'Diese Nähe ermöglicht eine realistische Einschätzung von Anforderungen, Risiken und Erwartungen auf Unternehmens- und Vorstandsebene.'
      },
      partnership: {
        title: 'Partnerschaftliches Selbstverständnis',
        text: 'Unser Ziel ist eine langfristige, verlässliche Zusammenarbeit mit kleinen, mittleren und großen Unternehmen im DACH-Raum. Wir verstehen uns als:',
        roles: [
          'technischer Verantwortungspartner für CFO, CIO und CTO',
          'Architekt stabiler und steuerbarer IT-Strukturen',
          'Unterstützer nachhaltiger Effizienz, Kostenkontrolle und Weiterentwicklung'
        ],
        summary: 'Integrova AI steht für sachliche, verantwortbare IT-Lösungen in einer neuen technologischen und wirtschaftlichen Realität.'
      },
      values: {
        title: 'Unsere Werte',
        items: [
          { name: 'Exzellenz', subline: 'Enterprise-Grade Delivery' },
          { name: 'Sicherheit', subline: 'ISO 27001 & TISAX Audit-Ready' },
          { name: 'Innovation', subline: 'AI-First Architektur' },
          { name: 'Transparenz', subline: 'Volle Kosten- & Risikotransparenz' },
          { name: 'Impact', subline: 'Durchschn. 5:1 ROI im ersten Jahr' }
        ]
      }
    },
    servicesOverview: {
      sectionTitle: 'Leistungsseiten',
      services: [
        { title: 'Change Management & Delivery', href: '/services/change-management-delivery' },
        { title: 'Nachhaltigkeit & ESG Tech', href: '/services/sustainability-esg-tech' },
        { title: 'Security, Privacy & Governance', href: '/services/security-privacy-governance' },
        { title: 'KI & Automatisierung', href: '/services/ai-automation' },
        { title: 'ERP & SAP', href: '/services/erp-sap' },
        { title: 'Softwareentwicklung', href: '/services/software-engineering' },
        { title: 'Cloud & Platform Foundations', href: '/services/cloud-platform-foundations' },
      ],
    },
    industries: {
      tag: 'Branchen',
      title: 'Branchen, die wir bedienen',
      subtitle: 'Ausgewählte Erfolgsgeschichten unserer Partner.',
      items: [
        {
          icon: 'Shield',
          title: 'Automotive',
          description: 'Vernetzte Fahrzeuge & Smart Manufacturing.',
        },
        {
          icon: 'Activity',
          title: 'MedTech & Pharma',
          description: 'GxP-Compliance & digitale Gesundheit.',
        },
        {
          icon: 'Landmark',
          title: 'Banking & Insurance',
          description: 'DORA-Compliance & Betrugserkennung.',
        },
        {
          icon: 'Zap',
          title: 'Energie & Utilities',
          description: 'Kritische Infrastruktur (KRITIS).',
        }
      ],
    },
    contact: {
      tag: 'Strategic Assessment',
      title: 'Kostenloses Strategic Enterprise Assessment',
      subtitle:
        '5–10 Minuten • Sie erhalten: Executive Summary (PDF) + 90-Tage-Roadmap + ROI-Schätzung',
      helper: 'Lieber erst sprechen? Buchen Sie ein 15-Min-Kennenlerngespräch oder senden Sie unten eine Nachricht.',
      form: {
        name: 'Name',
        email: 'E-Mail',
        company: 'Unternehmen',
        phone: 'Telefon',
        subject: 'Betreff',
        message: 'Ihre Nachricht',
        submit: 'Nachricht senden',
        consentLabel: 'Ich stimme der Verarbeitung meiner Daten gemäß der',
        consentLink: 'Datenschutzerklärung',
        consentLabelSuffix: 'zu.',
        consentRequired: 'Bitte bestätigen Sie die Datenschutzerklärung.',
        workEmailError: 'Bitte verwenden Sie eine geschäftliche E-Mail-Adresse.',
        submitError: 'Nachricht nicht gesendet. Bitte erneut versuchen oder an info@integrovaai.de schreiben.',
        submitErrorButton: 'Fehler – bitte erneut versuchen',
        calendlyCta: 'Kontakt via Calendly buchen',
        secureBadge: 'Sicheres Formular · DSGVO-konform',
        subjects: {
          consultation: 'Strategische Beratung',
          project: 'Projektanfrage',
          partnership: 'Partnerschaft',
          other: 'Sonstiges',
        },
      },
      info: {
        address: 'München, Deutschland',
        email: 'info@integrovaai.de',
      },
    },
    footer: {
      company: 'Integrova AI',
      description: 'Strategic Corporate Solution Provider.',
      links: {
        company: 'Unternehmen',
        legal: 'Rechtliches',
        social: 'Social',
        privacy: 'Datenschutz',
        impressum: 'Impressum',
      },
      copyright: '© 2025 Integrova AI. Alle Rechte vorbehalten.',
    },
    data: {
      title: 'Daten',
      items: [],
    },
    methodology: {
      title: 'Die Integrova-Methodik',
      subtitle: 'Von Insight zu Impact – ein bewährter 4-Phasen-Prozess',
      phases: {
        discover: {
          title: 'Entdecken',
          copy: 'Wir beginnen mit einer tiefgehenden, datengetriebenen Analyse Ihrer aktuellen Landschaft: Asset-Inventare, Prozessabläufe, regulatorische Lücken und kulturelle Bereitschaft. Unser proprietäres Executive-Resource-Center fasst KPI-Level-Insights in einem einzigen Dashboard zusammen.'
        },
        design: {
          title: 'Design',
          copy: 'Auf Basis der Analyse entwickeln wir gemeinsam eine zukünftige Architektur, die Strategie, Technologie und Compliance vereint. Das beinhaltet einen Fahrplan, Business-Case und ein risikogerechnetes ROI-Modell.'
        },
        deploy: {
          title: 'Umsetzen',
          copy: 'Wir setzen den Fahrplan mit agiler, modularer Lieferung um: Pilot-first, schnelle Skalierung und kontinuierliche Integration. Unsere Clean-Core-Surgeon- und License-Defense-Tools garantieren minimale Störungen und maximale Governance.'
        },
        evolve: {
          title: 'Weiterentwickeln',
          copy: 'Nach der Umsetzung verankern wir einen kontinuierlichen Verbesserungs-Loop (KPls, automatisierte Reports, KI-gestützte Empfehlungen). Das Regional-Hero-Messaging und der ROI-PDF-Generator halten Stakeholder informiert und das Business anpassungsfähig.'
        }
      },
      pillars: {
        governance: 'Governance & Compliance – End-to-End-Policies, audit-fertige Nachweise, DORA/NIS2-Bereitschaft.',
        data: 'Daten-getriebene Entscheidungen – Echtzeit-Dashboards, Peer-Benchmarking, KI-gestützte Insights.',
        technology: 'Technologie-Enablement – Cloud-native Plattformen, Clean-Core-Modernisierung, sichere Lizenzierung.'
      },
      cta: {
        headline: 'Bereit für die Transformation?',
        button: 'Kostenlose Beratung anfragen',
        subtext: 'Erhalten Sie eine unverbindliche Analyse und sehen Sie, welchen ROI die Integrova-Methodik für Ihr Unternehmen liefert.'
      }
    },
    accessibility: {
      shortcuts: {
        title: 'Tastaturkürzel',
        navigation: 'Navigation',
        toggleMenu: 'Menü umschalten',
        scrollDown: 'Nach unten scrollen',
        general: 'Allgemein',
        showHelp: 'Hilfe anzeigen',
      },
    },
  },
  en: {
    nav: {
      about: 'About',
      services: 'Services',
      aiAgents: 'AI Agents',
      process: 'Process',
      industries: 'Industries',
      contact: 'Contact',
      consultation: 'Book Consultation',
    },
    hero: {
      badge: 'Strategic Enterprise IT Partner',
      title: 'Enterprise IT Solutions That Drive Measurable ROI',
      titleHighlight: '',
      subtitle:
        'Modernizing Enterprise Systems for DACH Leaders. Enhanced Security. Measurable Efficiency.',
      cta1: 'Start Free Strategic Assessment',
      cta2: 'How does it work?',
      gcc: {
        badge: 'Strategic Enterprise IT Partner for the Gulf',
        title: 'Enterprise IT Solutions That Drive Vision 2030',
        subtitle: 'Partnering with C-Level decision-makers in the GCC to modernize systems, enhance security, and drive digital transformation.',
      },
    },
    clientLogos: {
      header: 'Delivering Enterprise Excellence with Industry Leaders',
      enterpriseTitle: 'Strategic Technology Partners',
      assuranceTitle: 'Certified Trust',
      enterpriseItems: [
        { name: 'SAP', logo: '/images/partners/sap.svg' },
        { name: 'Microsoft', logo: '/images/partners/microsoft.svg' },
        { name: 'VMware', logo: '/images/partners/vmware.svg' },
        { name: 'Salesforce', logo: '/images/partners/salesforce.svg' },
        { name: 'Docker', logo: '/images/partners/docker.png' },
        { name: 'Kubernetes', logo: '/images/partners/kubernetes.png' },
        { name: 'GitHub', logo: '/images/partners/github.png' },
        { name: 'GitLab', logo: '/images/partners/gitlab.png' },
        { name: 'PostgreSQL', logo: '/images/partners/postgresql.png' },
        { name: 'Apache Kafka', logo: '/images/partners/kafka.png' },
        { name: 'ISO 27001 Certified', logo: '/images/partners/iso27001.png' },
        { name: 'AWS Partner', logo: '/images/partners/aws.svg' },
      ],
      assuranceItems: [
        'Certified Cloud Experts',
        'Audit-Ready Compliance',
        'Enterprise Security',
        'GDPR Compliant',
        'Secure Infrastructure',
      ],
      assuranceItemsGCC: [
        'Certified Cloud Experts',
        'Audit-Ready Compliance',
        'Enterprise Security',
        'ADGM Compliant',
        'Secure Infrastructure',
      ],
    },
    strategicAssessment: {
      tag: 'Strategic Analysis',
      title: 'Free IT Assessment',
      subtitle: 'Get immediate insights into your IT environment with our free assessments.',
      prompt: 'Choose Assessment Focus',
      scoreLabel: 'Readiness Score',
      scoreSuffix: '/100',
      disclaimer: 'Non-binding initial assessment based on your inputs.',
      cta: 'Start Assessment',
      options: [
        {
          id: 'modernization',
          title: 'IT Modernization',
          description: 'Assess your legacy systems and modernization potential.',
          score: 0,
          insight: 'Start your modernization journey.',
        },
        {
          id: 'governance',
          title: 'Governance & Security',
          description: 'Evaluate your compliance and security posture.',
          score: 0,
          insight: 'Strengthen your governance.',
        },
      ],
    },
    diagnostic: {
      tag: 'Free Assessment',
      title: 'Enterprise Maturity Assessment',
      subtitle: 'Benchmark your organization against DACH peers. 10 strategic questions, instant PDF report with actionable recommendations.',
      startBtn: 'Start Assessment',
      noEmail: 'No email required • Instant PDF download',
      progress: 'Progress',
      estimatedTime: '~3 minutes',
      analyzing: {
        title: 'Analyzing Enterprise Architecture...',
        step1: 'Evaluating Compliance Maturity...',
        step2: 'Analyzing Integration Landscape...',
        step3: 'Comparing with Industry Benchmarks...',
        step4: 'Generating Recommendations...',
      },
      result: {
        scoreTitle: 'Your Maturity Score',
        peerTitle: 'DACH Average',
        downloadBtn: 'Download Report (PDF)',
        downloading: 'Generating PDF...',
        note: 'Your report includes dimension-specific analysis and prioritized recommendations.',
        scheduleBtn: 'Discuss Results',
        restartBtn: 'Restart',
      },
      dimensions: {
        regulatory: {
          name: 'Regulatory Readiness',
          description: 'DORA, NIS2 & EU AI Act compliance capability',
        },
        integration: {
          name: 'Integration Architecture',
          description: 'Legacy systems, technical debt & modernization',
        },
        ai_governance: {
          name: 'AI Governance',
          description: 'Control over AI usage & Shadow AI risks',
        },
        visibility: {
          name: 'Operational Visibility',
          description: 'Real-time insight into assets, costs & dependencies',
        },
        strategy: {
          name: 'Strategic Alignment',
          description: 'SAP roadmap, Clean Core & transformation planning',
        },
      },
      questions: [
        {
          id: 'reg_reporting',
          dimension: 'regulatory',
          text: 'How is your DORA/NIS2 compliance reporting currently handled?',
          context: 'From January 2025, affected companies must maintain demonstrable processes.',
          options: [
            {
              value: 20,
              label: 'Automated with real-time dashboards',
              insight: 'Exemplary automation – reduces audit effort by up to 70%',
            },
            {
              value: 15,
              label: 'Semi-automated, quarterly reports',
              insight: 'Good foundation, but manual gaps increase risk during ad-hoc audits',
            },
            {
              value: 8,
              label: 'Mostly manual via Excel/Confluence',
              insight: 'Typical for mid-market, but not scalable for DORA requirements',
            },
            {
              value: 0,
              label: 'No structured process in place',
              insight: 'Critical gap – regulatory risk from Q1 2025',
            },
          ],
        },
        {
          id: 'reg_ict_risk',
          dimension: 'regulatory',
          text: 'Can you document ICT risks from third parties (cloud, SaaS, outsourcing) in an auditable manner?',
          options: [
            {
              value: 20,
              label: 'Complete vendor risk management with continuous monitoring',
              insight: 'DORA-compliant – enables rapid response to third-party incidents',
            },
            {
              value: 12,
              label: 'Contracts and SLAs documented, no active monitoring',
              insight: 'Documentation exists, but reactive posture during disruptions',
            },
            {
              value: 5,
              label: 'Partially documented, depends on vendor',
              insight: 'Fragmented view increases risk during critical service outages',
            },
            {
              value: 0,
              label: 'No systematic tracking',
              insight: 'Essential DORA requirement not met',
            },
          ],
        },
        {
          id: 'int_legacy',
          dimension: 'integration',
          text: 'How many of your business-critical processes depend on legacy interfaces (BAPI, RFC, IDOC, flat files)?',
          options: [
            {
              value: 20,
              label: 'Under 20% – predominantly modern APIs',
              insight: 'Future-proof architecture with low technical debt',
            },
            {
              value: 12,
              label: '20-50% – mixed landscape',
              insight: 'Typical for SAP customers – prioritized migration recommended',
            },
            {
              value: 5,
              label: '50-80% – legacy dominates',
              insight: 'Significant integration debt – blocks innovation velocity',
            },
            {
              value: 0,
              label: 'Over 80% – barely any modern interfaces',
              insight: 'Critical dependency on expertise that is increasingly hard to find',
            },
          ],
        },
        {
          id: 'int_documentation',
          dimension: 'integration',
          text: 'Is your interface landscape fully documented and accessible to developers?',
          options: [
            {
              value: 20,
              label: 'Yes, API catalog with OpenAPI specs and self-service portal',
              insight: 'Enables rapid onboarding of new developers and partners',
            },
            {
              value: 12,
              label: 'Partially documented in Confluence/SharePoint',
              insight: 'Documentation exists, but search and currency are challenges',
            },
            {
              value: 5,
              label: 'Only in the heads of experts',
              insight: 'Critical knowledge risk – key person dependencies',
            },
            {
              value: 0,
              label: 'Unknown / No overview',
              insight: 'Shadow integration risk – nobody knows the complete landscape',
            },
          ],
        },
        {
          id: 'ai_inventory',
          dimension: 'ai_governance',
          text: 'Do you have an overview of all AI tools and LLMs being used in your organization?',
          context: 'EU AI Act requires transparency about AI usage from 2025.',
          options: [
            {
              value: 20,
              label: 'Complete AI inventory with risk assessment per use case',
              insight: 'EU AI Act ready – foundation for responsible AI scaling',
            },
            {
              value: 12,
              label: 'Official tools known, but no Shadow AI tracking',
              insight: 'Governance gap for non-approved tools (ChatGPT, Copilot etc.)',
            },
            {
              value: 5,
              label: 'Only IT-managed projects captured',
              insight: 'Business units likely using their own tools without oversight',
            },
            {
              value: 0,
              label: 'No central overview',
              insight: 'Significant compliance and data protection risk from uncontrolled AI usage',
            },
          ],
        },
        {
          id: 'ai_production',
          dimension: 'ai_governance',
          text: 'Are AI/ML models integrated into your productive business processes?',
          options: [
            {
              value: 20,
              label: 'Yes, multiple use cases in production with MLOps pipeline',
              insight: 'Competitive advantage through operationalized AI',
            },
            {
              value: 15,
              label: 'Individual use cases live, without standardized pipeline',
              insight: 'Good start, but scalability and maintainability are risks',
            },
            {
              value: 8,
              label: 'Only pilots and PoCs, nothing in production',
              insight: 'Typical "PoC trap" – missing path to productionization',
            },
            {
              value: 0,
              label: 'No active AI usage',
              insight: 'Competitive disadvantage in industries with increasing AI adoption',
            },
          ],
        },
        {
          id: 'vis_assets',
          dimension: 'visibility',
          text: 'Do you maintain an automated inventory of all software assets and cloud resources?',
          options: [
            {
              value: 20,
              label: 'Yes, automated with CMDB integration and dependency mapping',
              insight: 'Prerequisite for incident response and change management',
            },
            {
              value: 12,
              label: 'Semi-automated, but not all systems captured',
              insight: 'Blind spots increase MTTR during incidents',
            },
            {
              value: 5,
              label: 'Manually maintained lists (Excel, ServiceNow)',
              insight: 'High maintenance effort, currency questionable',
            },
            {
              value: 0,
              label: 'No systematic tracking',
              insight: 'DORA requirement not met – no complete picture of attack surface',
            },
          ],
        },
        {
          id: 'vis_costs',
          dimension: 'visibility',
          text: 'Do you have real-time visibility into cloud costs and SaaS spending?',
          options: [
            {
              value: 20,
              label: 'Yes, FinOps dashboard with anomaly detection and forecasting',
              insight: 'Enables proactive cost optimization – typically 20-30% savings potential',
            },
            {
              value: 12,
              label: 'Monthly analyses, but no real-time view',
              insight: 'Reactive cost control – surprises at billing time',
            },
            {
              value: 5,
              label: 'Only aggregated figures from accounting',
              insight: 'No attribution to teams/projects – cost allocation missing',
            },
            {
              value: 0,
              label: 'No dedicated cost overview',
              insight: 'Uncontrolled cloud cost growth – often 30-50% over budget',
            },
          ],
        },
        {
          id: 'str_sap',
          dimension: 'strategy',
          text: 'How is your SAP S/4HANA roadmap defined regarding Clean Core?',
          context: 'Clean Core is critical for update capability and support after 2027.',
          options: [
            {
              value: 20,
              label: 'Strict Clean Core strategy with side-by-side extensions',
              insight: 'Future-proof architecture – enables continuous SAP updates',
            },
            {
              value: 15,
              label: 'Clean Core planned, migration in progress',
              insight: 'On track – prioritize critical custom code remediation',
            },
            {
              value: 8,
              label: 'S/4HANA roadmap exists, Clean Core not prioritized',
              insight: 'Risk: Embedded extensions will complicate future upgrades',
            },
            {
              value: 0,
              label: 'Still on ECC / No clear S/4 strategy',
              insight: 'Pressure from SAP ECC support end 2027/2030',
            },
          ],
        },
        {
          id: 'str_transform',
          dimension: 'strategy',
          text: 'How would you rate your ability to implement new business requirements on the IT side?',
          options: [
            {
              value: 20,
              label: 'Agile – new features in weeks, not months',
              insight: 'Competitively relevant time-to-market',
            },
            {
              value: 12,
              label: 'Average – depends on project size and legacy dependencies',
              insight: 'Typical for grown landscapes – selective modernization recommended',
            },
            {
              value: 5,
              label: 'Slow – often 6+ months for medium changes',
              insight: 'Integration debt slows innovation',
            },
            {
              value: 0,
              label: 'Very limited – IT is primarily in "keep the lights on" mode',
              insight: 'Strategic capability at risk',
            },
          ],
        },
      ],
    },
    roiCalculator: {
      title: 'Calculate Your ROI',
      subtitle: 'See how much OpEx you could save with strategic modernization.',
      spendLabel: 'Current Annual IT Spend',
      savingLabel: 'Potential OpEx Savings',
      emailPlaceholder: 'Work Email Address',
      cta: 'Get Full ROI Report',
      disclaimer: 'Estimation based on industry benchmarks.',
      industryLabel: 'Industry',
      industries: {
        general: 'General',
        finance: 'Banking & Finance',
        automotive: 'Automotive',
        retail: 'Retail',
      },
    },
    productSuite: {
      tag: 'Strategic Focus Areas',
      title: 'Three Programs for Enterprise Excellence',
      subtitle: 'Predictable costs, strong governance, and modern ERP landscapes.',
      items: [
        {
          eyebrow: 'Legacy Modernization',
          title: 'Software Asset Optimization',
          subtitle: 'Reduce technical debt and optimize license costs.',
          bullets: ['License Optimization', 'Code Refactoring', 'Cloud Migration'],
          cta: 'Learn More',
          href: '/services/software-engineering',
        },
        {
          eyebrow: 'Governance',
          title: 'Digital Governance Core',
          subtitle: 'Ensure compliance and security across your IT landscape.',
          bullets: ['DORA Compliance', 'ISO 27001 Readiness', 'Risk Management'],
          cta: 'Learn More',
          href: '/services/security-privacy-governance',
        },
        {
          eyebrow: 'ERP Strategy',
          title: 'SAP & ERP Modernization',
          subtitle: 'Future-proof your enterprise resource planning.',
          bullets: ['S/4HANA Migration', 'Process Automation', 'Data Strategy'],
          cta: 'Learn More',
          href: '/services/erp-sap',
        },
      ],
    },
    keyCapabilities: {
      title: 'Service Portfolio & Delivery',
      items: [
        { title: 'AI Automation & Analytics', link: '/services/ai-automation' },
        { title: 'ERP & SAP Integration', link: '/services/erp-sap' },
        { title: 'Enterprise Software Engineering', link: '/services/software-engineering' },
        { title: 'Cloud & Platform Foundations', link: '/services/cloud-platform-foundations' },
        { title: 'Security, Privacy & Governance', link: '/services/security-privacy-governance' },
        { title: 'Change Management & Delivery', link: '/services/change-management-delivery' },
      ],
    },
    about: {
      tag: 'Strategic Partner',
      title: 'Strategic',
      titleHighlight: 'Solution Provider',
      description1: 'Engineering-first approach that reduced tech debt for 10+ DACH leaders. Average project savings of 28%.',
      description2: 'Value-Driven Engineering across software assets, governance, and ERP modernization – with measurable results: 100% audit success rate and 60% faster time-to-value on average.',
      card1Title: 'Enterprise Value',
      card1Text: 'Make OpEx predictable and secure budgets long-term. Avg. 5:1 ROI in Year 1.',
      card2Title: 'Governance Excellence',
      card2Text: 'ISO 27001 & TISAX Audit-Ready. 100% compliance success rate for our clients.',
    },
    servicesOverview: {
      sectionTitle: 'Service Pages',
      services: [
        { title: 'Change Management & Delivery', href: '/services/change-management-delivery' },
        { title: 'Sustainability & ESG Tech', href: '/services/sustainability-esg-tech' },
        { title: 'Security, Privacy & Governance', href: '/services/security-privacy-governance' },
        { title: 'AI & Automation', href: '/services/ai-automation' },
        { title: 'ERP & SAP', href: '/services/erp-sap' },
        { title: 'Software Development', href: '/services/software-engineering' },
        { title: 'Cloud & Platform Foundations', href: '/services/cloud-platform-foundations' },
      ],
    },
    services: {
      tag: 'Core Competencies',
      title: 'Tailored Enterprise Services',
      subtitle: 'Scalable solutions for your digital transformation.',
      hero: {
        title: 'Strategic Enterprise Services',
        subtitle: 'Advisory and delivery for software assets, governance, and ERP modernization.',
        cta: 'View Service Portfolio',
      },
      pillars: {
        strategy: {
          title: 'Strategy & Transformation',
          items: [
            'Enterprise roadmap (12–18 months) with priorities, milestones, and investment plan',
            'Portfolio & cost baseline (apps, vendors, licenses) to reduce OpEx leakage',
            'Operating model: decision rights, KPIs/OKRs, governance cadence',
            'Transformation PMO: delivery plan, RAID, steering pack, executive reporting',
            'Change enablement: stakeholder map, comms plan, training & adoption metrics',
          ],
        },
        ai: {
          title: 'AI & Data Analytics',
          items: [
            'AI strategy & use cases with ROI model and delivery backlog',
            'Data foundation: quality, MDM, catalog/lineage, data ownership model',
            'Analytics layer: KPI definitions, semantic model, BI acceleration',
            'MLOps/LLMOps: deployment, monitoring, drift, model governance',
            'Responsible AI & compliance guardrails (GDPR + risk controls)',
          ],
        },
        erp: {
          title: 'SAP & ERP Transformation',
          items: [
            'S/4HANA roadmap & target architecture with migration waves',
            'Clean-Core strategy + extensibility blueprint (APIs, events, BTP)',
            'Process harmonization & template design (fit-to-standard)',
            'Integration & API layer (data contracts, orchestration, interfaces)',
            'Risk & audit readiness: documentation, controls, and governance setup',
          ],
        },
        software: {
          title: 'Digital Workplace',
          items: [
            'Enterprise apps & portals (employee journeys, self-service workflows)',
            'Collaboration automation (approvals, onboarding, service flows)',
            'Identity & access modernization (SSO/MFA/RBAC)',
            'Adoption & enablement (training, champions, usage analytics)',
            'Security-by-design policies (DLP, access rules, governance)',
          ],
        },
      },
      outcomes: {
        title: 'Outcomes',
        items: [
          'Predictable costs and clear responsibilities',
          'Audit-ready governance and compliance',
          'Modernized ERP landscapes',
          'Data-driven decision-making',
          'Transparency across systems and vendors',
        ],
      },
      software: {
        title: 'Custom Software Development',
        items: [
          'Tailored Java & Enterprise stacks.',
          'Scalable frontend & backend architectures.',
          'Performance-critical applications.',
        ],
      },
      cloud: {
        title: 'Cloud & DevOps Solutions',
        items: [
          'Migration and infrastructure automation.',
          'Reduces deployment time by 50%.',
          'Cost-optimized cloud architectures.',
        ],
      },
      erp: {
        title: 'SAP Modernization',
        items: [
          'Efficiency gains through system upgrades.',
          'Process optimization and Clean Core.',
          'Better data insights.',
        ],
      },
      ai: {
        title: 'AI & Data Strategy',
        items: [
          'Intelligent agents for decision-making.',
          'Data platforms for automation.',
          'Measurable ROI scenarios.',
        ],
      },
      grc: {
        title: 'Governance, Risk & Compliance',
        items: [
          'IT security audits and frameworks.',
          'DORA/NIS2 compliance readiness.',
          'Integrated risk management.',
        ],
      },
      changeManagement: {
        title: 'AI Adoption & Change Management',
        items: [
          'Workforce-ready AI & Culture Gap.',
          'Prosci-aligned adoption frameworks.',
          'Secure Works Council buy-in.',
        ],
        itemsGCC: [
          'Workforce-ready AI & Culture Gap.',
          'Prosci-aligned adoption frameworks.',
          'PDPL-aligned privacy proxy.',
        ],
      },
    },
    aiAgents: {
      tag: 'AI Agents',
      title: 'Intelligent Agents for Your Enterprise',
      subtitle:
        'Custom AI agents that integrate directly into SAP, ERP, and enterprise systems',
      agents: {
        document: {
          title: 'Document Processing',
          desc: 'Automated extraction & classification',
        },
        logistics: {
          title: 'Logistics Assistant',
          desc: 'Intelligent routing & tracking',
        },
        supply: {
          title: 'Supply Chain Forecasting',
          desc: 'Predictive inventory management',
        },
        finance: {
          title: 'Finance Copilot',
          desc: 'Automated reports & analytics',
        },
        field: {
          title: 'Field Service Copilot',
          desc: 'Mobile scheduling & support',
        },
        support: {
          title: 'Customer Support AI',
          desc: 'Intelligent 24/7 customer service',
        },
      },
      tech: {
        llm: 'LLMs (GPT architectures)',
        rag: 'RAG Pipelines',
        cloud: 'Cloud & On-Premise',
      },
    },
    process: {
      tag: 'Process',
      title: 'Our 6-Step Process',
      subtitle: 'From strategy to scalable execution',
      steps: {
        step1: {
          num: '01',
          title: 'Discovery & Strategy',
          subtitle: 'Aligning technology with business goals',
          items: ['Needs Assessment', 'Feasibility Study', 'Strategic Roadmap'],
        },
        step2: {
          num: '02',
          title: 'Analysis & Architecture',
          subtitle: 'Designing the digital ecosystem',
          items: ['System Audit', 'Solution Architecture', 'Tech Stack Selection'],
        },
        step3: {
          num: '03',
          title: 'Development & Integration',
          subtitle: 'Building the core infrastructure',
          items: ['Agile Development', 'API Integration', 'Secure Coding'],
        },
        step4: {
          num: '04',
          title: 'AI Model Training',
          subtitle: 'Implementing intelligence',
          items: ['Data Preparation', 'Model Fine-Tuning', 'Performance Optimization'],
        },
        step5: {
          num: '05',
          title: 'Testing & Validation',
          subtitle: 'Ensuring reliability and security',
          items: ['QA Testing', 'Security Audits', 'User Acceptance Testing'],
        },
        step6: {
          num: '06',
          title: 'Deployment & Scaling',
          subtitle: 'Going live and growing',
          items: ['Seamless Deployment', 'Monitoring', 'Continuous Improvement'],
        },
      },
    },
    outcomes: {
      tag: 'Value Impact',
      title: 'Measurable value for boards and business units',
      subtitle: 'Predictable costs, resilient governance, modern platforms.',
      items: [
        {
          icon: 'piggyBank',
          kpi: '30%',
          title: 'OpEx Reduction',
          desc: 'Software asset baselines create cost transparency and reduce unplanned expenses.',
          link: '/products/license-defense'
        },
        {
          icon: 'shield',
          kpi: '100%',
          title: 'DORA Readiness',
          desc: 'NIS2/DORA-aligned processes as a competitive advantage – before 2025 enforcement.',
          link: '/products/officer-shield'
        },
        {
          icon: 'settings',
          kpi: '60%',
          title: 'Z-Code Risk Reduced',
          desc: 'Clean-core S/4HANA modernization with a clear roadmap and measurable progress.',
          link: '/products/clean-core-surgeon'
        },
        {
          icon: 'award',
          kpi: 'ISO 27001',
          title: 'Enterprise Standards',
          desc: 'Security, compliance, and audit readiness by default – not as an afterthought.',
          link: '/trust'
        },
      ],
    },
    proof: {
      tag: 'Trust & Compliance',
      title: 'Enterprise standards & certifications',
      subtitle: 'Verified success in regulated sectors.',
      artifacts: {
        title: 'Enterprise standards',
        subtitle: 'Accelerates approvals and vendor due diligence.',
        items: [
          { title: 'DPA & AVV', desc: 'GDPR-aligned templates including TOMs.' },
          { title: 'Security Overview', desc: 'Controls, IAM, encryption, and logging.' },
          { title: 'ISO-aligned policies', desc: 'ISO/BSI-aligned policies and runbooks.' },
        ],
      },
      stats: [
        { value: '€250k–€2M', label: 'OpEx planning per asset review' },
        { value: '4h', label: 'Regulatory reporting (DORA/NIS2)' },
        { value: '30–45 days', label: 'to a strategic roadmap' },
        { value: '60%', label: 'Z-code reduced pre-S/4HANA' },
      ],
      labels: {
        boardroomChallenge: 'Boardroom challenge',
        impact: 'Executive impact',
      },
      actions: {
        cta: 'Book a strategic consultation',
        readCase: 'Read Brief',
        downloadCase: 'Strategy Brief (PDF)',
        downloadSecurity: 'Security Overview (PDF)',
        note: 'References available under NDA; metrics are anonymized ranges.',
      },
      cases: [
        {
          title: 'Industrial group',
          industry: 'Manufacturing',
          badge: 'ISO Certified',
          metric: '28%',
          metricLabel: 'Consolidation',
          boardroomChallenge: 'VMware price volatility threatened 12% of the IT budget.',
          impact: 'Secured a 3-year pricing framework and delivered 28% consolidation.',
        },
        {
          title: 'Insurance group',
          industry: 'Regulated',
          badge: 'Audited Report',
          metric: '100%',
          metricLabel: 'Audit-Ready',
          boardroomChallenge: 'DORA/NIS2 reporting was fragmented and not audit-ready.',
          impact: 'Enabled <4-hour regulatory reporting and audit-ready policies.',
        },
        {
          title: 'Global manufacturer',
          industry: 'SAP',
          badge: 'SAP Gold Partner',
          metric: '60%',
          metricLabel: 'Z-Code Reduced',
          boardroomChallenge: 'S/4HANA program faced uncontrolled custom code risk.',
          impact: 'Identified 60% of Z-code candidates and secured the 2027 roadmap.',
        },
      ],
      certificates: {
        title: 'Certified Security & Compliance',
        items: [
          { name: 'ISO 27001', icon: 'ShieldCheck' },
          { name: 'TISAX Ready', icon: 'Car' },
          { name: 'SOC 2 Type II', icon: 'FileCheck2' },
        ],
      },
    },
    engagement: {
      tag: 'Engagement',
      title: 'Strategic roadmap, clear delivery',
      subtitle: 'Assessment, blueprint, delivery — without overhead.',
      steps: [
        { title: 'Assessment (2–4 weeks)', desc: 'Asset and process analysis, stakeholder alignment.' },
        { title: 'Strategic Blueprint (4–6 weeks)', desc: 'Sourcing, governance, and modernization plan.' },
        { title: 'Delivery & enablement (ongoing)', desc: 'Implementation, PMO integration, steering committee reporting.' },
      ],
      personas: [
        'CFO/CIO/CISO as executive sponsors',
        'Procurement & vendor management',
        'GRC, risk & internal audit',
        'SAP/ERP program leadership',
      ],
      budgets: [
        'Asset management audit: €25–60k',
        'GRC framework setup: €60–150k',
        'ERP modernization assessment: €120–350k',
        'Enterprise rollout: scope-based',
      ],
    },
    homeProcess: {
      tag: 'Operating Model',
      title: 'Value-driven delivery',
      subtitle: 'Assessment, blueprint, execution — aligned to enterprise programs.',
      steps: [
        {
          title: 'Assessment',
          subtitle: 'Cost and risk transparency',
          items: ['Asset & license readout', 'Regulatory gap analysis', 'Stakeholder alignment'],
        },
        {
          title: 'Blueprint',
          subtitle: 'Strategic target state',
          items: ['Sourcing & GRC design', 'ERP modernization plan', 'Business case'],
        },
        {
          title: 'Execution',
          subtitle: 'Governed implementation',
          items: [
            'Programs & workstreams',
            'PMO integration & steering committee reporting',
            'Operating model & runbooks',
          ],
        },
      ],
    },
    industries: {
      tag: 'Industries',
      title: 'Industries We Serve',
      subtitle: 'Deep understanding of your specific challenges',
      items: [
        {
          icon: 'Shield',
          title: 'Automotive',
          description: 'Connected Cars & Smart Manufacturing.',
        },
        {
          icon: 'Activity',
          title: 'MedTech & Pharma',
          description: 'GxP Compliance & Digital Health.',
        },
        {
          icon: 'Landmark',
          title: 'Banking & Insurance',
          description: 'DORA Compliance & Fraud Detection.',
        },
        {
          icon: 'Zap',
          title: 'Energy & Utilities',
          description: 'Critical Infrastructure (KRITIS).',
        }
      ],
    },
    contact: {
      tag: 'Strategic Assessment',
      title: 'Free Strategic Enterprise Assessment',
      subtitle:
        '5–10 minutes • You’ll receive: Executive summary PDF + 90-day roadmap + ROI estimate',
      helper: 'Prefer to talk first? Book a 15-min intro call or send a message below.',
      form: {
        name: 'Name',
        email: 'Work Email',
        company: 'Company',
        phone: 'Phone',
        subject: 'Subject',
        message: 'Your Message',
        submit: 'Send Message',
        consentLabel: 'I agree to the processing of my data in accordance with the',
        consentLink: 'Privacy Policy',
        consentLabelSuffix: '.',
        consentRequired: 'Please confirm the privacy policy.',
        workEmailError: 'Please use a business email address.',
        submitError: 'Message not sent. Please try again or email info@integrovaai.de.',
        submitErrorButton: 'Error – try again',
        calendlyCta: 'Book via Calendly',
        secureBadge: 'Secure form · GDPR-ready',
        subjects: {
          consultation: 'Strategic consultation',
          project: 'Project Inquiry',
          partnership: 'Partnership',
          other: 'Other',
        },
      },
      info: {
        address: 'Munich, Germany',
        email: 'info@integrovaai.de',
      },
      consultation: {
        title: 'Free Consultation',
        desc: '30 min strategy session with an expert.',
        btn: 'Book Appointment'
      }
    },
    insightsTeaser: {
      tag: 'Executive Resource Center',
      title: 'Briefs for CFO, CISO, and CIO',
      subtitle:
        'Persona-specific decision support for risk, cost, and transformation.',
      cta: 'Read the brief →',
      items: [
        {
          eyebrow: 'For CFOs',
          title: 'OpEx transparency through data quality & governance',
          desc: 'How data quality stabilizes budget and risk planning.',
          href: '/insights/data-quality-ai',
        },
        {
          eyebrow: 'For CISOs',
          title: 'DORA/NIS2 readiness as a compliance program',
          desc: 'Controls, evidence, and reporting for audit readiness.',
          href: '/insights/nis2-gdpr-ai',
        },
        {
          eyebrow: 'For CIOs',
          title: 'SAP modernization as a clean-core program',
          desc: 'Reduce Z-code and secure your S/4HANA roadmap.',
          href: '/insights/sap-ai-modernization',
        },
      ],
    },
    footer: {
      company: 'Integrova AI',
      description: 'Strategic Corporate Solution Provider.',
      links: {
        company: 'Company',
        legal: 'Legal',
        social: 'Social',
        privacy: 'Privacy Policy',
        impressum: 'Legal Notice',
      },
      copyright: '© 2025 Integrova AI. All rights reserved.',
    },
    data: {
      title: 'Data Strategy',
      items: [
        'Data Governance',
        'Master Data',
        'Quality Frameworks',
        'Data Operating Model',
      ],
    },
    aiAutomationPage: {
      hero: {
        title: 'AI Agents & Enterprise Automation — Built for Real-World Operations',
        subtitle: 'We develop intelligent AI systems that automate decisions, optimize workflows, and unlock measurable efficiency gains.',
      },
      capabilities: {
        title: 'Key Capabilities',
        items: [
          { title: 'Enterprise AI Agents', desc: 'Autonomous agents for complex cross-system tasks.' },
          { title: 'NLP Chatbots', desc: 'Natural language processing for customer service and support.' },
          { title: 'Predictive Analytics', desc: 'Data-driven forecasts for better decision making.' },
          { title: 'Document Processing', desc: 'Automated extraction from invoices and contracts.' },
          { title: 'Workflow Automation', desc: 'Seamless integration into ERP and CRM landscapes.' },
        ],
      },
      outcomes: {
        title: 'Outcomes',
        items: [
          '40–70% reduction in repetitive workload',
          'Real-time insights instead of manual reporting',
          'Reduced operational costs',
          'Higher speed and accuracy in business processes',
        ],
      },
      process: {
        title: 'Process',
        steps: [
          'AI Readiness Assessment',
          'Data & System Integration',
          'Model Selection & Fine-tuning',
          'Deployment & Guardrails',
          'Monitoring & Iteration',
        ],
      },
      faq: {
        title: 'FAQ',
        items: [
          { q: 'How do AI Agents integrate with SAP?', a: 'Via secure APIs and connectors.' },
          { q: 'Can you deploy on-premise for sensitive data?', a: 'Yes, we support on-premise and private cloud.' },
          { q: 'What guardrails ensure AI behavior?', a: 'We use strict validation layers and human-in-the-loop.' },
          { q: 'How long does a typical AI project take?', a: 'Depending on scope, 4–12 weeks for an MVP.' },
        ],
      },
    },
    erpSapPage: {
      hero: {
        title: 'Modernize and Extend Your SAP Landscape with AI & Cloud',
        subtitle: 'We help enterprises unlock the full value of SAP through intelligent automation, workflow engineering, and seamless integration.',
      },
      capabilities: {
        title: 'Capabilities',
        items: [
          { title: 'SAP S/4HANA Engineering', desc: 'Custom development and adaptation.' },
          { title: 'SAP Fiori UX', desc: 'Modern, user-friendly interfaces.' },
          { title: 'API Integration', desc: 'Secure connection of SAP with third-party systems.' },
          { title: 'SAP BTP Extensions', desc: 'Extending SAP functionality in the cloud.' },
          { title: 'Migration Support', desc: 'Support for transformation to S/4HANA.' },
          { title: 'Automated Workflows', desc: 'Efficiency gains through intelligent processes.' },
        ],
      },
      outcomes: {
        title: 'Outcomes',
        items: [
          'Faster business processes',
          'Improved data consistency',
          'Enhanced user experience',
          'Reduced operational bottlenecks',
          'Lower technical debt',
        ],
      },
      process: {
        title: 'Process',
        steps: [
          'SAP Landscape Review',
          'Integration Blueprint',
          'Custom Development',
          'Testing & Validation',
          'Handover & Enablement',
        ],
      },
      faq: {
        title: 'FAQ',
        items: [
          { q: 'Do you support hybrid SAP environments?', a: 'Yes, we are experts in hybrid architectures.' },
          { q: 'Can you build custom SAP Fiori apps?', a: 'Absolutely, we design and develop Fiori apps.' },
          { q: 'How do you integrate AI into SAP?', a: 'Through SAP BTP or external AI services via API.' },
        ],
      },
    },
    softwareDevPage: {
      hero: {
        title: 'Enterprise Software for Scale, Security & Performance',
        subtitle: 'We design and deliver mission-critical applications tailored to your business processes.',
      },
      capabilities: {
        title: 'Capabilities',
        items: [
          { title: 'Web Applications', desc: 'Scalable and secure web solutions.' },
          { title: 'Enterprise Systems', desc: 'Robust software for mission-critical processes.' },
          { title: 'Mobile Apps', desc: 'Native and cross-platform apps for iOS and Android.' },
          { title: 'API & Microservices', desc: 'Modular architectures for flexibility.' },
          { title: 'System Integration', desc: 'Connecting isolated systems into one unit.' },
          { title: 'DevOps & Cloud', desc: 'Automated deployments and infrastructure.' },
        ],
      },
      outcomes: {
        title: 'Outcomes',
        items: [
          'Scalable architecture',
          'Reduced time-to-market',
          'Increased reliability',
          'Seamless integration across systems',
        ],
      },
    },
    industriesPage: {
      hero: {
        title: 'AI-Powered Solutions Across Key Industries',
        subtitle: 'We understand the specific challenges of your industry.',
      },
      items: [
        { title: 'Automotive', desc: 'Predictive maintenance, logistics optimization' },
        { title: 'Energy', desc: 'Smart grid analytics, forecasting' },
        { title: 'Logistics', desc: 'Warehouse automation, routing' },
        { title: 'Fintech', desc: 'Fraud detection, compliance automation' },
        { title: 'Manufacturing', desc: 'IIoT analytics, automation' },
      ],
      pillars: {
        automotive: {
          title: 'Automotive & Mobility',
          items: ['Predictive Maintenance', 'Supply Chain Optimization', 'AI Quality Assurance', 'SAP Integration for OEMs'],
        },
        energy: {
          title: 'Energy & Renewables',
          items: ['Smart Grid Analytics', 'Demand Forecasting', 'Asset Performance Management', 'Grid Optimization'],
        },
        logistics: {
          title: 'Logistics & Warehousing',
          items: ['Warehouse Automation', 'Route Optimization', 'Inventory Forecasting', 'Real-time Tracking'],
        },
        fintech: {
          title: 'Fintech & Banking',
          items: ['Fraud Detection', 'Compliance Automation', 'Risk Assessment', 'KYC Processes'],
        },
        manufacturing: {
          title: 'Manufacturing & IIoT',
          items: ['Production Optimization', 'Machine Monitoring', 'Quality Control', 'Digital Twin'],
        },
      },
    },
    companyPage: {
      hero: {
        title: 'About Us',
        subtitle: 'Accountable IT Expertise for CFO, CIO, and CTO',
        headline: "We're not a PowerPoint consultancy. We're architects of the new IT era.",
        subheadline: 'Integrova AI closes the gap between technological potential and operational reality.',
        intro: 'We deliver measurable ROI through engineering-first methodology. No slides, no theory – only proven results for DACH enterprise clients.',
      },
      story: {
        title: 'Why Integrova AI was founded',
        content: 'Integrova AI was founded because of a growing gap between classic IT service models and the actual requirements of modern enterprises. Many organizations have faced the same structural problems for years:',
        problems: [
          'High IT costs due to inefficient service provider and project models',
          'Long implementation times despite available technologies',
          'Limited productivity and efficiency gains despite rising IT budgets',
          'Lack of controlled implementation of AI in existing enterprise landscapes'
        ],
      },
      reality: {
        title: 'A New Reality for Enterprise IT',
        content: 'Technological developments in AI, increasing economic pressure, and regulatory changes have fundamentally altered the conditions for IT. For companies, this means:',
        implications: [
          'Higher requirements for cost control and predictability',
          'Expectations of fast, reliable implementation',
          'Need to reduce complexity and operating costs',
          'Use of AI according to clearly defined corporate rules',
          'Clean integration of existing systems',
          'Controlled evolution of SAP and ERP landscapes'
        ],
      },
      focus: {
        text: 'Our focus is to re-establish IT as a controllable, economically viable, and sustainable function within the company.'
      },
      differentiation: {
        title: 'Clear Differentiation',
        items: [
          'no PowerPoint consulting without implementation responsibility',
          'not just a pure software development service provider',
          'no provider of isolated pilot or experiment projects',
          'We take responsibility for architecture, integration, and long-term operability of IT systems.'
        ]
      },
      principles: {
        title: 'Technological Principles',
        items: [
          'Artificial Intelligence is used only when results are traceable and explainable',
          'Governance is technically anchored in the architecture, not compensated organizationally',
          'Automation is strictly controlled, verifiable, and reproducible'
        ],
        goal: 'The goal is to reduce risks, secure decision-making processes, and make operational dependencies manageable.'
      },
      team: {
        title: 'Experience over Size',
        content: 'Integrova AI is a young company with an experienced core team of software engineers and IT consultants who have worked in various corporate environments.',
        workingStyle: [
          'Use of proven, modern technologies',
          'Focus on stability, maintainability, and quality',
          'Structured, efficient implementation'
        ],
        growth: 'Growth is consciously controlled. Technical quality and reliability come before rapid scaling.'
      },
      dach: {
        title: 'Anchored in the DACH Region',
        content: [
          'Our team studied and worked in the DACH region for many years.',
          'We are familiar with the linguistic, cultural, organizational, and regulatory conditions of this market.'
        ],
        benefit: 'This proximity allows for a realistic assessment of requirements, risks, and expectations at the corporate and board levels.'
      },
      partnership: {
        title: 'Partnership Mindset',
        text: 'Our goal is long-term, reliable cooperation with small, medium, and large companies in the DACH region. We see ourselves as:',
        roles: [
          'technical accountability partner for CFO, CIO, and CTO',
          'Architect of stable and controllable IT structures',
          'Supporter of sustainable efficiency, cost control, and development'
        ],
        summary: 'Integrova AI stands for objective, accountable IT solutions in a new technological and economic reality.'
      },
      values: {
        title: 'Our Values',
        items: [
          { name: 'Excellence', subline: 'Enterprise-Grade Delivery' },
          { name: 'Security', subline: 'ISO 27001 & TISAX Audit-Ready' },
          { name: 'Innovation', subline: 'AI-First Architecture' },
          { name: 'Transparency', subline: 'Full Cost & Risk Visibility' },
          { name: 'Impact', subline: 'Avg. 5:1 ROI in Year 1' }
        ]
      }
    },
    methodology: {
      title: 'The Integrova Methodology',
      subtitle: 'From Insight to Impact – a Proven 4-Phase Journey',
      phases: {
        discover: {
          title: 'Discover',
          copy: 'We start with a deep, data-driven discovery of your current landscape: asset inventories, process flows, regulatory gaps, and cultural readiness. Our proprietary Executive Resource Center aggregates KPI-level insights into a single, visual dashboard.'
        },
        design: {
          title: 'Design',
          copy: 'Using the discovery artefacts, we co-create a future-state architecture that aligns strategy, technology, and compliance. This includes a roadmap, business case, and risk-adjusted ROI model.'
        },
        deploy: {
          title: 'Deploy',
          copy: 'We execute the roadmap with agile, modular delivery: pilot-first, rapid-scale, and continuous integration. Our Clean-Core Surgeon and License-Defense tools guarantee minimal disruption and maximum governance.'
        },
        evolve: {
          title: 'Evolve',
          copy: 'Post-deployment we embed a continuous-improvement loop (KPIs, automated reporting, AI-driven recommendations). The Regional Hero Messaging and ROI-PDF Generator keep stakeholders informed and the business adaptable.'
        }
      },
      pillars: {
        governance: 'Governance & Compliance – End-to-end policies, audit-ready evidence, DORA/NIS2 readiness.',
        data: 'Data-Driven Decisioning – Real-time dashboards, benchmark-based peer scoring, AI-enabled insights.',
        technology: 'Technology Enablement – Cloud-native platforms, clean-core modernization, secure licensing.'
      },
      cta: {
        headline: 'Ready to transform?',
        button: 'Request a Free Assessment',
        subtext: 'Get a free, no-obligation assessment and see how the Integrova Methodology can deliver measurable ROI for your enterprise.'
      }
    },
    accessibility: {
      shortcuts: {
        title: 'Keyboard Shortcuts',
        navigation: 'Navigation',
        toggleMenu: 'Toggle Menu',
        scrollDown: 'Scroll Down',
        general: 'General',
        showHelp: 'Show Help',
      },
    },
  },
};
